import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ArrowDownToLine, CheckCircle, Link2, Lock, Plus, RotateCcw, X } from 'lucide-react';
import PageSkeleton from '../common/Skeleton';
import {
  Button,
  AlertMessage,
  PageHeader,
  Select,
  Modal,
  Textarea,
  ConfirmModal,
} from '../common/ui';
import { formatDateTime } from '../../utils/dateUtils';
import { computePaymentReconciliation } from '../../utils/paymentReconciliation';
import { getWooCommerceOrderUrl } from './types';
import type { VascoLink, OrderVascoDocument } from './types';
import PaymentInternalNotes from './PaymentInternalNotes';
import VascoDocumentRow, { VascoChip } from './VascoDocumentRow';
import { PaymentGkBox, usePaymentGk, gkExceptionsFor, GkRowMark } from './PaymentGkBox';

const PAYMENT_SOURCES = [
  { value: 'Bank transfer', label: 'Bank transfer' },
  { value: 'Revolut Credit Card', label: 'Revolut Credit Card' },
  { value: 'Braintree', label: 'Braintree' },
  { value: 'Apple', label: 'Apple' },
  { value: 'Google', label: 'Google' },
  { value: 'PayPal', label: 'PayPal' },
];

// Vasco document numbers are "RACUN/LETO", e.g. "3814/2026".
const VASCO_NUMBER_RE = /^\d+\/\d{4}$/;

/**
 * Format a Vasco invoice date for display.
 *
 * The date comes from the Vasco export as day.month.year text, e.g. "20.3.2026"
 * (see server/utils/parseVasco.js). JavaScript reads that as month.day.year, so
 * `new Date('20.3.2026')` is an invalid date and `new Date('1.5.2026')` is
 * 5 January instead of 1 May. The parts therefore have to be split by hand.
 *
 * Text that is not a day.month.year date is returned unchanged — better to show
 * what is stored than to show "Invalid Date".
 */
export const formatVascoDate = (raw: string): string => {
  const parts = raw.replace(/\s+/g, '').replace(/\.$/, '').split('.');
  if (parts.length === 3 && parts.every((p) => /^\d+$/.test(p))) {
    const day = Number(parts[0]);
    const month = Number(parts[1]);
    const year = parts[2].length <= 2 ? 2000 + Number(parts[2]) : Number(parts[2]);
    const date = new Date(year, month - 1, day);
    // getDate() catches an impossible day (31.2.2026), which Date rolls over.
    if (date.getDate() === day && date.getMonth() === month - 1) {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
  return raw;
};

interface VascoCreditNote {
  number: string;
  amount: number;
}

interface LinkedOrder {
  id: number;
  wooOrderId: number;
  orderNumber: string;
  customerName: string;
  total: string;
  currency: string;
  vascoInvoice: string | null;
  /** How much of this order is covered by this particular payment. Null = full order total (legacy). */
  partialAmount: string | null;
  processingFee: string | null;
  settlementAmount: string | null;
  /**
   * How many statement settlements this one link stands for (SW-2496).
   * >1 = the customer was charged repeatedly for the order inside this payout,
   * and settlementAmount is their sum. 0 for non-Revolut sources. Optional so
   * older payloads and test fixtures that omit it still read as "not multiple".
   */
  settlementCount?: number;
  /** Item-level force close (SW-2489). Non-null forceClosedAt = resolved by hand. */
  forceClosedAt: string | null;
  forceCloseNote: string | null;
  forceClosedByName: string | null;
  /**
   * Split charge: this order is paid by the Revolut charge of another order
   * linked to the same payment. Its settlementAmount is its share, with no fee.
   * Optional so older payloads and test fixtures read as "not split".
   */
  splitChargeOrderId?: number | null;
  splitChargeOrderNumber?: string | null;
  splitNote?: string | null;
  splitAt?: string | null;
  splitByName?: string | null;
  creditNotes: VascoCreditNote[];
}

interface PaypalGrossMismatch {
  orderId: number;
  orderNumber: string;
  paypalTxnId: string;
  currency: string;
  paypalGross: number;
  wooTotal: number;
  delta: number;
}

interface RevolutOrderTotalMismatch {
  orderId: number;
  orderNumber: string;
  revolutTxnId: string;
  currency: string;
  revolutGross: number;
  wooTotal: number | null;
  delta: number | null;
  customerName: string | null;
  customerEmail: string | null;
  orderDescription: string | null;
  orderChannel: string | null;
  externalOrderId: string | null;
}

interface PaypalRefundActivity {
  orderId: number | null;
  orderNumber: string | null;
  wooOrderId: number | null;
  customerName: string | null;
  // Revolut refund rows fall back to the CSV's Customer email + Order
  // description so unlinked refunds still show a meaningful identifier.
  // PayPal/Braintree variants leave these null.
  customerEmail?: string | null;
  orderDescription?: string | null;
  wooTotal: number | null;
  refundTxnId: string;
  referenceTxnId: string | null;
  currency: string;
  refundGross: number;
  refundNet: number;
  saleTxnId: string | null;
  saleGross: number | null;
  saleFee: number | null;
  saleNet: number | null;
  referencedPaymentId: number | null;
}

// Revolut "Transfer" rows used to top the merchant balance up to fund a
// refund whose original sale settled in a different payout. They are part
// of the payout balance but neither a direct payment nor a direct refund.
interface RevolutRefundFundingActivity {
  orderId: number | null;
  orderNumber: string | null;
  wooOrderId: number | null;
  customerName: string | null;
  wooTotal: number | null;
  transferTxnId: string;
  referenceTxnId: string | null;
  currency: string;
  amount: number;
  refundTxnId: string | null;
  refundGross: number | null;
  refundNet: number | null;
  saleTxnId: string | null;
  saleNet: number | null;
  referencedPaymentId: number | null;
}

interface PaypalSummary {
  transactionCount: number;
  saleCount: number;
  refundCount: number;
  salesGross: number;
  salesFees: number;
  salesNet: number;
  refundsGross: number;
  refundsNet: number;
  totalNet: number;
  grossMismatches: PaypalGrossMismatch[];
  refundActivities: PaypalRefundActivity[];
}

interface RevolutUnlinkedSettlement {
  revolutTxnId: string;
  orderId: number | null;
  orderNumber: string | null;
  orderAssignedByHand?: boolean;
  currency: string;
  revolutGross: number;
  settlementAmount: number | null;
  wooTotal: number | null;
  orderTotalMismatch: boolean;
  reason: 'order_not_found' | 'linked_elsewhere' | 'currency_mismatch' | 'unlinked';
  linkedElsewherePaymentId: number | null;
  customerName: string | null;
  customerEmail: string | null;
  orderDescription: string | null;
  orderChannel: string | null;
  externalOrderId: string | null;
  forceClosedAt: string | null;
  forceCloseNote: string | null;
  forceClosedByName: string | null;
}

interface RevolutSummary {
  transactionCount: number;
  saleCount: number;
  refundCount: number;
  refundFundingCount: number;
  salesGross: number;
  salesFees: number;
  salesNet: number;
  refundsGross: number;
  refundsNet: number;
  refundFundingNet: number;
  totalNet: number;
  mismatchCount: number;
  orderTotalMismatches: RevolutOrderTotalMismatch[];
  refundActivities: PaypalRefundActivity[];
  refundFundingActivities: RevolutRefundFundingActivity[];
  unlinkedSettlements: RevolutUnlinkedSettlement[];
  unlinkedSettlementsNet: number;
}

interface BraintreeOrderTotalMismatch {
  orderId: number;
  orderNumber: string;
  braintreeTxnId: string;
  currency: string;
  braintreeGross: number;
  braintreeNet: number | null;
  wooTotal: number | null;
  delta: number | null;
}

interface BraintreeUnlinkedSettlement {
  braintreeTxnId: string;
  orderId: number | null;
  orderNumber: string | null;
  currency: string;
  braintreeGross: number;
  net: number | null;
  wooTotal: number | null;
  orderTotalMismatch: boolean;
  settlementDate: string;
  reason: 'order_not_found' | 'linked_elsewhere' | 'currency_mismatch' | 'unlinked';
  linkedElsewherePaymentId: number | null;
  forceClosedAt: string | null;
  forceCloseNote: string | null;
  forceClosedByName: string | null;
}

// AIB refunds share the AIB Transaction Order ID with their original sale,
// so the server pairs them by `order_number`. Shape is compatible with
// PaypalRefundActivity for the refundGroups renderer.
interface BraintreeRefundActivity {
  orderId: number | null;
  orderNumber: string | null;
  wooOrderId: number | null;
  customerName: string | null;
  wooTotal: number | null;
  refundTxnId: string;
  currency: string;
  refundGross: number;
  refundNet: number;
  settlementDate: string;
  saleTxnId: string | null;
  saleGross: number | null;
  saleFee: number | null;
  saleNet: number | null;
  referencedPaymentId: number | null;
  orderTotalMismatch: boolean;
}

// An AIB chargeback adjustment: a chargeback debit and/or the flat chargeback
// handling fee. AIB reports it either fully standalone (no Transaction ID, no
// Settlement Date) or attached to the disputed sale, repeating that sale's
// Transaction ID. Either way it settles nothing of its own, so it is listed
// here rather than as an unlinked settlement, and its net is a known deduction
// from the payout.
interface BraintreeFeeAdjustment {
  braintreeTxnId: string;
  currency: string;
  settlementDate: string;
  net: number;
  chargebackCaseNumber: string | null;
  chargebackAmount: number;
  chargebackFeeAmount: number;
  chargebackDebitCurrency: string | null;
  chargebackFeeCurrency: string | null;
  /** The disputed sale, when AIB attached the chargeback to it. Display only. */
  sourceTransactionId: string | null;
  sourceOrderNumber: string | null;
  processingDate: string;
  merchantName: string | null;
  merchantAccountToken: string | null;
  mid: string | null;
}

interface BraintreeSummary {
  transactionCount: number;
  saleCount: number;
  refundCount: number;
  salesGross: number;
  salesFees: number;
  salesNet: number;
  refundsGross: number;
  refundsNet: number;
  feeOnlyCount?: number;
  feesNet?: number;
  feeAdjustments?: BraintreeFeeAdjustment[];
  totalNet: number;
  mismatchCount: number;
  orderTotalMismatches: BraintreeOrderTotalMismatch[];
  refundActivities: BraintreeRefundActivity[];
  unlinkedSettlements: BraintreeUnlinkedSettlement[];
  unlinkedSettlementsNet: number;
}

interface OrderMismatch {
  orderId: number;
  orderNumber: string;
  currency: string;
  txnGross: number;
  wooTotal: number | null;
  delta: number | null;
  sourceLabel: 'PayPal' | 'Revolut' | 'Braintree';
  /** AIB/Revolut net used for payout reconciliation (Braintree/Revolut mismatches). */
  statementNet?: number | null;
  customerName?: string | null;
  customerEmail?: string | null;
  orderDescription?: string | null;
  orderChannel?: string | null;
}

// Unified shape used by the unlinkedSettlements UI section. Both Revolut and
// Braintree contribute rows here; the source label drives which fee/gross
// label appears in badges.
interface UnlinkedSettlement {
  forceClosedAt: string | null;
  forceCloseNote: string | null;
  forceClosedByName: string | null;
  txnId: string;
  orderId: number | null;
  orderNumber: string | null;
  // Revolut-only: the order was assigned by hand and has no link on this
  // payment, so it can be taken back off the settlement.
  orderAssignedByHand: boolean;
  currency: string;
  gross: number;
  net: number | null;
  wooTotal: number | null;
  orderTotalMismatch: boolean;
  reason: 'order_not_found' | 'linked_elsewhere' | 'currency_mismatch' | 'unlinked';
  linkedElsewherePaymentId: number | null;
  sourceLabel: 'Revolut' | 'Braintree';
  // Revolut populates these from the CSV; Braintree leaves them null until we
  // backfill the same metadata from its reconciliation file.
  customerName: string | null;
  customerEmail: string | null;
  orderDescription: string | null;
  // Revolut-only: the CSV "Order channel" (e.g. CARD_READER for POS terminal)
  // and the Revolut "Order ID" UUID. Help identify settlements that carry no
  // order reference. Braintree leaves these null.
  orderChannel: string | null;
  externalOrderId: string | null;
}

interface PaypalRefundGroup {
  key: string;
  orderId: number | null;
  orderNumber: string | null;
  wooOrderId: number | null;
  customerName: string | null;
  customerEmail: string | null;
  orderDescription: string | null;
  wooTotal: number | null;
  currency: string;
  activities: PaypalRefundActivity[];
  refundGross: number;
  refundNet: number;
  saleGross: number | null;
  saleNet: number | null;
  referencedPaymentId: number | null;
}

/** Coerce b2b order ids — pg/node-pg may return number or string depending on query path. */
function normalizeOrderId(id: number | string | null | undefined): number {
  return Number(id);
}

interface CandidateOrder {
  id: number;
  woo_order_id: number;
  order_number: string;
  customer_name: string | null;
  company: string | null;
  total: number | null;
  currency: string;
  order_date: string | null;
  status: string | null;
  tier: 0 | 1 | 2 | 3;
  /** Sum of partial_amounts already linked to this order (0 = unlinked). Used to show remaining balance. */
  covered_amount: number;
}

interface AlreadyClaimedEntry {
  order_id: number;
  woo_order_id: number;
  order_number: string;
  invoice_number: string | null;
  customer_name: string | null;
  total: number | null;
  order_currency: string;
  order_status: string | null;
  linked_payment_id: number;
  linked_payment_amount: number;
  linked_payment_currency: string;
  linked_payment_date: string;
  linked_payment_counterparty: string | null;
  linked_payment_remittance_info: string | null;
}

// GET /vasco-invoices/lookup response (SW-2486). `found: false` still carries
// free-text claims other payments made on that number.
interface VascoLookupClaim {
  paymentId: number;
  /** Effective claimed amount (explicit claim, or the full invoice amount). */
  amount: number;
  paymentCurrency: string;
  paymentDate: string;
  counterparty: string | null;
  verified: boolean;
}

interface VascoLookupInvoice {
  id: number;
  vascoInvoice: string;
  invoiceDate: string | null;
  orderNumber: string | null;
  buyer: string | null;
  recipient: string | null;
  /** EUR; null when the import row carried no ZNESEK. */
  amount: number | null;
  paymentMethod: string | null;
  isCreditNote: boolean;
}

interface VascoLookupResult {
  found: boolean;
  invoice?: VascoLookupInvoice | null;
  linkedToOrder?: boolean;
  claims: VascoLookupClaim[];
  claimedTotal?: number;
  /** Invoice amount minus other payments' claims; null when the amount is unknown. */
  remainder?: number | null;
  alreadyLinkedToThisPayment?: boolean;
}

/**
 * Item-level force close target (SW-2489): either a linked order (note lives
 * on payment_order_links) or an unlinked Revolut/Braintree settlement (note
 * lives in payment_settlement_resolutions).
 */
type ItemForceCloseTarget =
  | { kind: 'link'; orderId: number }
  | { kind: 'settlement'; source: 'revolut' | 'braintree'; txnId: string };

interface PaymentDetailData {
  id: number;
  acctSvcrRef: string;
  date: string;
  amount: number;
  currency: string;
  type: string;
  counterparty: string | null;
  description: string | null;
  remittanceInfo: string | null;
  paymentSource: string | null;
  linkedStatus: string;
  reconciliationNote: string | null;
  forceClosedAt: string | null;
  forceClosedByName: string | null;
  forceCloseNote: string | null;
  linkedOrders: LinkedOrder[];
  vascoLinks: VascoLink[];
  /** Vasco documents of the linked orders — read-only here, see the section below. */
  orderVascoDocuments: OrderVascoDocument[];
  paypalSummary: PaypalSummary | null;
  revolutSummary: RevolutSummary | null;
  braintreeSummary: BraintreeSummary | null;
  revolutCarryoverIn: number | null;
  revolutCarryoverOut: number | null;
  settlementFee: number;
}

const PaymentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [payment, setPayment] = useState<PaymentDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Distinguishes a true 404 (payment doesn't exist) from a transient 500 /
  // network error so the empty-state UI can show the correct message and a
  // retry affordance instead of misleadingly claiming the payment is missing.
  const [notFound, setNotFound] = useState(false);
  const [loadErrorStatus, setLoadErrorStatus] = useState<number | null>(null);
  const [success, setSuccess] = useState('');

  const [orderInput, setOrderInput] = useState('');
  const [partialAmountInput, setPartialAmountInput] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linking, setLinking] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  // Vasco invoice linking (SW-2486)
  const [vascoNumberInput, setVascoNumberInput] = useState('');
  const [vascoAmountInput, setVascoAmountInput] = useState('');
  // True once the user typed into the amount field — blocks remainder pre-fill overwrites.
  const [vascoAmountTouched, setVascoAmountTouched] = useState(false);
  const [vascoLinkError, setVascoLinkError] = useState('');
  const [vascoLinking, setVascoLinking] = useState(false);
  const [removingVascoLinkId, setRemovingVascoLinkId] = useState<number | null>(null);
  const [vascoLookup, setVascoLookup] = useState<VascoLookupResult | null>(null);
  const [vascoLookupLoading, setVascoLookupLoading] = useState(false);

  // Force close / reopen
  const [showForceCloseModal, setShowForceCloseModal] = useState(false);
  const [forceCloseNote, setForceCloseNote] = useState('');
  const [forceClosing, setForceClosing] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopening, setReopening] = useState(false);

  // Item-level force close (SW-2489) — one inline resolution form at a time.
  const [itemForceCloseTarget, setItemForceCloseTarget] = useState<ItemForceCloseTarget | null>(null);
  const [itemForceCloseNote, setItemForceCloseNote] = useState('');
  const [itemForceClosing, setItemForceClosing] = useState(false);
  const [itemForceCloseError, setItemForceCloseError] = useState('');
  const [reopeningItemKey, setReopeningItemKey] = useState<string | null>(null);

  // Split charge: one Revolut charge paid several orders. splitTargetOrderId is
  // the order the statement names; the form asks which other order it paid.
  const [splitTargetOrderId, setSplitTargetOrderId] = useState<number | null>(null);
  const [splitOrderInput, setSplitOrderInput] = useState('');
  const [splitNoteInput, setSplitNoteInput] = useState('');
  const [splitting, setSplitting] = useState(false);
  const [splitError, setSplitError] = useState('');
  const [undoingSplitOrderId, setUndoingSplitOrderId] = useState<number | null>(null);

  const [candidates, setCandidates] = useState<CandidateOrder[]>([]);
  const [tier2Omitted, setTier2Omitted] = useState(0);
  const [alreadyClaimed, setAlreadyClaimed] = useState<AlreadyClaimedEntry[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [linkingCandidateId, setLinkingCandidateId] = useState<number | null>(null);

  // Per-unlinked-settlement manual order assignment (keyed by Revolut txn id).
  // POS / card-reader settlements carry no order reference so they can't
  // auto-link; the accountant types the order number here to bind it to this
  // specific settlement. See SW-2405.
  const [assignInputs, setAssignInputs] = useState<Record<string, string>>({});
  const [assignError, setAssignError] = useState<Record<string, string>>({});
  const [assigningTxnId, setAssigningTxnId] = useState<string | null>(null);
  const [removingOrderTxnId, setRemovingOrderTxnId] = useState<string | null>(null);

  const orderInputRef = useRef<HTMLInputElement>(null);

  // Each load of the payment loads its GK status again too.
  const [gkRefresh, setGkRefresh] = useState(0);
  const fetchPayment = useCallback(async (options: { showLoading?: boolean } = {}) => {
    const showLoading = options.showLoading !== false;
    if (showLoading) setLoading(true);
    try {
      const { data } = await axios.get<PaymentDetailData>(`/api/accounting/payments/${id}`);
      setPayment(data);
      setGkRefresh((k) => k + 1);
      setNotFound(false);
      setLoadErrorStatus(null);
    } catch (err: any) {
      const status = err?.response?.status ?? null;
      setLoadErrorStatus(status);
      // Only a true 404 means "this payment id doesn't exist". Server errors
      // (500, network failures, auth issues) leave the empty-state in retry
      // mode rather than misleading the user with "Payment Not Found".
      setNotFound(status === 404);
      setError(err?.response?.data?.error || 'Failed to load payment');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id]);

  const { data: gkData, error: gkError, changed: gkChanged } = usePaymentGk(id, gkRefresh);

  const fetchCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    try {
      const { data } = await axios.get<{
        candidates: CandidateOrder[];
        tier2Omitted: number;
        alreadyClaimed?: AlreadyClaimedEntry[];
      }>(`/api/accounting/payments/${id}/candidates`);
      setCandidates(data.candidates);
      setTier2Omitted(data.tier2Omitted);
      setAlreadyClaimed(data.alreadyClaimed || []);
    } catch {
      // Candidates are advisory — silently ignore errors
    } finally {
      setCandidatesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPayment();
    fetchCandidates();
  }, [fetchPayment, fetchCandidates]);

  // Debounced Vasco invoice lookup — fires once the number matches the
  // RACUN/LETO pattern; cleared as soon as it stops matching.
  useEffect(() => {
    const trimmed = vascoNumberInput.trim();
    if (!VASCO_NUMBER_RE.test(trimmed)) {
      setVascoLookup(null);
      setVascoLookupLoading(false);
      // Drop a stale remainder pre-fill; a hand-typed amount is kept.
      if (!vascoAmountTouched) setVascoAmountInput('');
      return;
    }
    setVascoLookupLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get<VascoLookupResult>('/api/accounting/vasco-invoices/lookup', {
          params: { number: trimmed, paymentId: id },
        });
        if (!cancelled) setVascoLookup(data);
      } catch {
        // Lookup is advisory — the POST endpoint revalidates anyway.
        if (!cancelled) setVascoLookup(null);
      } finally {
        if (!cancelled) setVascoLookupLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [vascoNumberInput, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill the amount with the unclaimed remainder (EUR payments only — Vasco
  // amounts are EUR) unless the user already typed an amount themselves.
  useEffect(() => {
    if (!vascoLookup?.found || vascoLookup.alreadyLinkedToThisPayment) return;
    if (vascoAmountTouched) return;
    if (payment?.currency !== 'EUR') return;
    const remainder = vascoLookup.remainder ?? 0;
    if (remainder > 0) setVascoAmountInput(remainder.toFixed(2));
  }, [vascoLookup]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSourceChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const paymentSource = e.target.value || null;
    if (!payment) return;
    setPayment({ ...payment, paymentSource });
    try {
      const { data } = await axios.patch(`/api/accounting/payments/${id}`, { paymentSource });
      setPayment((prev) => prev ? { ...prev, linkedStatus: data.linkedStatus } : prev);
      await fetchPayment({ showLoading: false });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save payment source');
    }
  };

  const handleForceClose = async () => {
    if (!forceCloseNote.trim() || forceClosing) return;
    setForceClosing(true);
    try {
      const { data } = await axios.post(`/api/accounting/payments/${id}/force-close`, {
        note: forceCloseNote.trim(),
      });
      setPayment((prev) => prev ? {
        ...prev,
        linkedStatus: data.linkedStatus,
        forceClosedAt: data.forceClosedAt,
        forceClosedByName: data.forceClosedByName,
        forceCloseNote: data.forceCloseNote,
      } : prev);
      setShowForceCloseModal(false);
      setForceCloseNote('');
      setSuccess('Payment force closed.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to force close payment');
    } finally {
      setForceClosing(false);
    }
  };

  const handleReopen = async () => {
    if (reopening) return;
    setReopening(true);
    try {
      const { data } = await axios.post(`/api/accounting/payments/${id}/reopen`, {});
      setPayment((prev) => prev ? { ...prev, linkedStatus: data.linkedStatus } : prev);
      setShowReopenModal(false);
      setSuccess('Payment reopened.');
      await fetchCandidates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reopen payment');
    } finally {
      setReopening(false);
    }
  };

  const itemForceCloseUrl = (target: ItemForceCloseTarget) =>
    target.kind === 'link'
      ? `/api/accounting/payments/${id}/links/${target.orderId}`
      : `/api/accounting/payments/${id}/settlements/${target.source}/${encodeURIComponent(target.txnId)}`;

  const openItemForceCloseForm = (target: ItemForceCloseTarget) => {
    setItemForceCloseTarget(target);
    setItemForceCloseNote('');
    setItemForceCloseError('');
  };

  const handleItemForceClose = async () => {
    if (!itemForceCloseTarget || !itemForceCloseNote.trim() || itemForceClosing) return;
    setItemForceClosing(true);
    setItemForceCloseError('');
    try {
      await axios.post(`${itemForceCloseUrl(itemForceCloseTarget)}/force-close`, {
        note: itemForceCloseNote.trim(),
      });
      setItemForceCloseTarget(null);
      setItemForceCloseNote('');
      setSuccess('Item force closed.');
      // Refetch everything — the item's card, the payment status and the
      // source summaries all change together.
      await fetchPayment({ showLoading: false });
      await fetchCandidates();
    } catch (err: any) {
      setItemForceCloseError(err.response?.data?.error || 'Failed to force close item');
    } finally {
      setItemForceClosing(false);
    }
  };

  const handleItemReopen = async (target: ItemForceCloseTarget) => {
    if (reopeningItemKey) return;
    const key = target.kind === 'link' ? `link-${target.orderId}` : `settlement-${target.txnId}`;
    setReopeningItemKey(key);
    try {
      await axios.post(`${itemForceCloseUrl(target)}/reopen`, {});
      setSuccess('Item reopened.');
      await fetchPayment({ showLoading: false });
      await fetchCandidates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reopen item');
    } finally {
      setReopeningItemKey(null);
    }
  };

  const linkOrderByNumber = async (orderNumber: string, partialAmount?: number, onSuccess?: () => void) => {
    try {
      const body: Record<string, unknown> = { orderNumber };
      if (partialAmount != null) body.partialAmount = partialAmount;
      const { data } = await axios.post(`/api/accounting/payments/${id}/link-order`, body);
      setPayment((prev) =>
        prev ? {
          ...prev,
          linkedOrders: data.linkedOrders,
          orderVascoDocuments: data.orderVascoDocuments ?? prev.orderVascoDocuments,
          linkedStatus: data.linkedStatus,
        } : prev
      );
      await fetchCandidates();
      onSuccess?.();
    } catch (err: any) {
      throw err;
    }
  };

  const handleLinkOrder = async () => {
    const trimmed = orderInput.trim();
    if (!trimmed) return;
    setLinkError('');
    setLinking(true);
    try {
      const pa = parseFloat(partialAmountInput);
      const partialAmount = !isNaN(pa) && pa > 0 ? pa : undefined;
      await linkOrderByNumber(trimmed, partialAmount, () => {
        setOrderInput('');
        setPartialAmountInput('');
        orderInputRef.current?.focus();
      });
    } catch (err: any) {
      setLinkError(err.response?.data?.error || 'Failed to link order');
    } finally {
      setLinking(false);
    }
  };

  const handleLinkCandidate = async (candidate: CandidateOrder) => {
    setLinkingCandidateId(candidate.id);
    setLinkError('');
    try {
      // If the order is partially covered by another payment, default to the remaining balance.
      const remaining = candidate.total != null && candidate.covered_amount > 0
        ? Math.max(0, candidate.total - candidate.covered_amount)
        : undefined;
      await linkOrderByNumber(candidate.order_number, remaining);
    } catch (err: any) {
      setLinkError(err.response?.data?.error || 'Failed to link order');
    } finally {
      setLinkingCandidateId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleLinkOrder();
  };

  const handleAssignOrder = async (revolutTxnId: string) => {
    const orderNumber = (assignInputs[revolutTxnId] || '').trim();
    if (!orderNumber) return;
    setAssigningTxnId(revolutTxnId);
    setAssignError((prev) => ({ ...prev, [revolutTxnId]: '' }));
    try {
      await axios.post(
        `/api/accounting/payments/${id}/revolut-settlements/${encodeURIComponent(revolutTxnId)}/assign-order`,
        { orderNumber }
      );
      setAssignInputs((prev) => {
        const next = { ...prev };
        delete next[revolutTxnId];
        return next;
      });
      await fetchPayment({ showLoading: false });
      await fetchCandidates();
    } catch (err: any) {
      setAssignError((prev) => ({
        ...prev,
        [revolutTxnId]: err.response?.data?.error || 'Failed to assign order',
      }));
    } finally {
      setAssigningTxnId(null);
    }
  };

  const handleRemoveAssignedOrder = async (revolutTxnId: string) => {
    setRemovingOrderTxnId(revolutTxnId);
    setAssignError((prev) => ({ ...prev, [revolutTxnId]: '' }));
    try {
      await axios.delete(
        `/api/accounting/payments/${id}/revolut-settlements/${encodeURIComponent(revolutTxnId)}/order`
      );
      await fetchPayment({ showLoading: false });
      await fetchCandidates();
    } catch (err: any) {
      setAssignError((prev) => ({
        ...prev,
        [revolutTxnId]: err.response?.data?.error || 'Failed to remove order',
      }));
    } finally {
      setRemovingOrderTxnId(null);
    }
  };

  const openSplitForm = (orderId: number) => {
    setSplitTargetOrderId(orderId);
    setSplitOrderInput('');
    setSplitNoteInput('');
    setSplitError('');
  };

  const handleSplitCharge = async () => {
    const orderNumber = splitOrderInput.trim();
    if (splitTargetOrderId == null || !orderNumber || splitting) return;
    setSplitting(true);
    setSplitError('');
    try {
      await axios.post(`/api/accounting/payments/${id}/links/${splitTargetOrderId}/split-charge`, {
        orderNumber,
        note: splitNoteInput.trim() || undefined,
      });
      setSplitTargetOrderId(null);
      setSuccess(`Split charge saved: order #${orderNumber.replace(/^#/, '')} is paid by the same Revolut charge.`);
      // The mismatch badges, the amounts and the status all change together.
      await fetchPayment({ showLoading: false });
      await fetchCandidates();
    } catch (err: any) {
      setSplitError(err.response?.data?.error || 'Failed to split charge');
    } finally {
      setSplitting(false);
    }
  };

  const handleUndoSplit = async (orderId: number) => {
    if (undoingSplitOrderId != null) return;
    setUndoingSplitOrderId(orderId);
    try {
      await axios.delete(`/api/accounting/payments/${id}/links/${orderId}/split-charge`);
      setSuccess('Split charge undone.');
      await fetchPayment({ showLoading: false });
      await fetchCandidates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to undo split charge');
    } finally {
      setUndoingSplitOrderId(null);
    }
  };

  const handleRemoveOrder = async (orderId: number) => {
    setRemovingId(orderId);
    // Removing either side of a split charge changes the other order's amount
    // and its mismatch badge, which only a full reload brings back.
    const touchesSplit = payment?.linkedOrders.some(
      (o) => (o.id === orderId && o.splitChargeOrderId != null) || o.splitChargeOrderId === orderId
    );
    try {
      const { data } = await axios.delete(`/api/accounting/payments/${id}/links/${orderId}`);
      setPayment((prev) =>
        prev ? {
          ...prev,
          linkedOrders: data.linkedOrders,
          orderVascoDocuments: data.orderVascoDocuments ?? prev.orderVascoDocuments,
          linkedStatus: data.linkedStatus,
        } : prev
      );
      if (touchesSplit) await fetchPayment({ showLoading: false });
      await fetchCandidates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove order link');
    } finally {
      setRemovingId(null);
    }
  };

  const handleLinkVasco = async () => {
    const invoiceNumber = vascoNumberInput.trim();
    if (!VASCO_NUMBER_RE.test(invoiceNumber)) return;
    setVascoLinkError('');
    setVascoLinking(true);
    try {
      const body: Record<string, unknown> = { invoiceNumber };
      // Blank = claim the full invoice amount. parseFloat keeps negative
      // amounts (credit notes) intact.
      const amt = parseFloat(vascoAmountInput);
      if (vascoAmountInput.trim() !== '' && !isNaN(amt)) body.amount = amt;
      const { data } = await axios.post(`/api/accounting/payments/${id}/link-vasco-invoice`, body);
      setPayment((prev) =>
        prev ? { ...prev, vascoLinks: data.vascoLinks, linkedStatus: data.linkedStatus } : prev
      );
      setVascoNumberInput('');
      setVascoAmountInput('');
      setVascoAmountTouched(false);
      setVascoLookup(null);
    } catch (err: any) {
      setVascoLinkError(err.response?.data?.error || 'Failed to link Vasco invoice');
    } finally {
      setVascoLinking(false);
    }
  };

  const handleRemoveVascoLink = async (linkId: number) => {
    setRemovingVascoLinkId(linkId);
    try {
      const { data } = await axios.delete(`/api/accounting/payments/${id}/vasco-links/${linkId}`);
      setPayment((prev) =>
        prev ? { ...prev, vascoLinks: data.vascoLinks, linkedStatus: data.linkedStatus } : prev
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove Vasco link');
    } finally {
      setRemovingVascoLinkId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Payment Detail</h1>
        <PageSkeleton rows={3} rowHeight="h-24" />
      </div>
    );
  }

  if (!payment) {
    if (notFound) {
      return (
        <div className="p-6">
          <div className="text-center py-12">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Not Found</h3>
            <Button variant="secondary" onClick={() => navigate('/accounting')} icon={undefined}>
              Back to Accounting
            </Button>
          </div>
        </div>
      );
    }
    const statusLabel = loadErrorStatus ? ` (HTTP ${loadErrorStatus})` : '';
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load payment{statusLabel}</h3>
          <p className="text-sm text-gray-600 mb-4">{error || 'The server returned an error while loading this payment.'}</p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="primary" onClick={() => fetchPayment()} icon={undefined}>
              Retry
            </Button>
            <Button variant="secondary" onClick={() => navigate('/accounting')} icon={undefined}>
              Back to Accounting
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasPartialOrders = payment.linkedOrders.some(
    (o) => o.partialAmount != null && parseFloat(o.partialAmount) < parseFloat(o.total)
  );
  const isForceClosed = payment.linkedStatus === 'force_closed';
  // Derived by the server (SW-2489): reconciled, but only because at least one
  // item was force closed. Rendered "Completed" like a payment force close.
  const isItemForceClosed = payment.linkedStatus === 'item_force_closed';
  // Force close is available for any incomplete payment (anything not linked and
  // not already force-closed) — e.g. a refund that can never match an order.
  const canForceClose = !['auto', 'manual', 'force_closed', 'item_force_closed'].includes(payment.linkedStatus);
  // Show the link panel unless the payment is fully reconciled AND has no partially-paid orders
  // that may still need a second installment linked (SW-2410). Never show it for a
  // force-closed payment.
  const showCandidates =
    (!(payment.linkedStatus === 'manual' || payment.linkedStatus === 'auto' || isItemForceClosed) || hasPartialOrders)
    && !isForceClosed;

  // The reconciliation math lives in utils/paymentReconciliation.ts. Its test
  // checks it against calcLinkedSummary on the server.
  const {
    hasFeesData,
    isRevolut,
    isBraintree,
    linkedGross,
    linkedFees,
    linkedNet,
    linkedSum,
    carryoverIn,
    carryoverOut,
    hasCarryover,
    refundFundingNet,
    settlementFee,
    vascoLinkedNet,
    forcedSettlementsNet,
    openUnlinkedNet,
    braintreeFeesNet,
    creditNotesNet,
    creditApplied,
    comparisonSum,
    remaining,
    isExact,
    isOverpaid,
    isUnderpaid,
  } = computePaymentReconciliation(payment);
  const paypalSummary = payment.paypalSummary;
  const revolutSummary = payment.revolutSummary;
  const braintreeSummary = payment.braintreeSummary;
  const txSummary = paypalSummary || revolutSummary || braintreeSummary;
  const txSourceLabel: 'PayPal' | 'Revolut' | 'Braintree' | null = paypalSummary
    ? 'PayPal'
    : revolutSummary
    ? 'Revolut'
    : braintreeSummary
    ? 'Braintree'
    : null;

  const orderMismatchByOrderId = new Map<number, OrderMismatch>();
  for (const m of paypalSummary?.grossMismatches || []) {
    orderMismatchByOrderId.set(normalizeOrderId(m.orderId), {
      orderId: normalizeOrderId(m.orderId),
      orderNumber: m.orderNumber,
      currency: m.currency,
      txnGross: m.paypalGross,
      wooTotal: m.wooTotal,
      delta: m.delta,
      sourceLabel: 'PayPal',
    });
  }
  for (const m of revolutSummary?.orderTotalMismatches || []) {
    orderMismatchByOrderId.set(normalizeOrderId(m.orderId), {
      orderId: normalizeOrderId(m.orderId),
      orderNumber: m.orderNumber,
      currency: m.currency,
      txnGross: m.revolutGross,
      wooTotal: m.wooTotal,
      delta: m.delta,
      sourceLabel: 'Revolut',
      customerName: m.customerName,
      customerEmail: m.customerEmail,
      orderDescription: m.orderDescription,
      orderChannel: m.orderChannel,
    });
  }
  for (const m of braintreeSummary?.orderTotalMismatches || []) {
    orderMismatchByOrderId.set(normalizeOrderId(m.orderId), {
      orderId: normalizeOrderId(m.orderId),
      orderNumber: m.orderNumber,
      currency: m.currency,
      txnGross: m.braintreeGross,
      wooTotal: m.wooTotal,
      delta: m.delta,
      sourceLabel: 'Braintree',
      statementNet: m.braintreeNet,
    });
  }

  const refundGroupsMap = new Map<string, PaypalRefundGroup>();
  // Braintree refund activities use `null` for sale-side fields (no
  // reference_txn_id, only order_number pairing) but are shape-compatible with
  // the PayPal refundGroups renderer via the missing `referenceTxnId` field.
  const braintreeRefundCompat = (braintreeSummary?.refundActivities || []).map((r) => ({
    orderId: r.orderId,
    orderNumber: r.orderNumber,
    wooOrderId: r.wooOrderId,
    customerName: r.customerName,
    customerEmail: null,
    orderDescription: null,
    wooTotal: r.wooTotal,
    refundTxnId: r.refundTxnId,
    referenceTxnId: null,
    currency: r.currency,
    refundGross: r.refundGross,
    refundNet: r.refundNet,
    saleTxnId: r.saleTxnId,
    saleGross: r.saleGross,
    saleFee: r.saleFee,
    saleNet: r.saleNet,
    referencedPaymentId: r.referencedPaymentId,
  } as PaypalRefundActivity));
  const rawRefundActivities = paypalSummary?.refundActivities
    || revolutSummary?.refundActivities
    || braintreeRefundCompat;
  // Two things have to be true before these are summed per order.
  //
  // One transaction, one entry: the server joins each refund to its sale row,
  // and a join that ever matches more than one sale would hand us the same
  // refund several times and multiply the order's refund total.
  //
  // A refund is an outflow, so its gross is negative here. The sources disagree:
  // PayPal sends it signed, Revolut and Braintree send ABS(). The code below
  // ADDS refundGross to the sale gross to get what was kept, so a positive value
  // would turn a full refund into double the sale.
  const seenRefundTxnIds = new Set<string>();
  const refundActivities = rawRefundActivities
    .filter((r) => {
      if (!r.refundTxnId) return true;
      if (seenRefundTxnIds.has(r.refundTxnId)) return false;
      seenRefundTxnIds.add(r.refundTxnId);
      return true;
    })
    .map((r) => ({ ...r, refundGross: -Math.abs(r.refundGross) }));
  for (const refund of refundActivities) {
    const key = refund.orderId !== null ? `order:${refund.orderId}` : `refund:${refund.refundTxnId}`;
    const existing = refundGroupsMap.get(key);
    if (existing) {
      existing.activities.push(refund);
      existing.refundGross += refund.refundGross;
      existing.refundNet += refund.refundNet;
      if (existing.saleGross === null && refund.saleGross !== null) existing.saleGross = refund.saleGross;
      if (existing.saleNet === null && refund.saleNet !== null) existing.saleNet = refund.saleNet;
      if (existing.referencedPaymentId === null && refund.referencedPaymentId !== null) existing.referencedPaymentId = refund.referencedPaymentId;
    } else {
      refundGroupsMap.set(key, {
        key,
        orderId: refund.orderId,
        orderNumber: refund.orderNumber,
        wooOrderId: refund.wooOrderId,
        customerName: refund.customerName,
        customerEmail: refund.customerEmail ?? null,
        orderDescription: refund.orderDescription ?? null,
        wooTotal: refund.wooTotal,
        currency: refund.currency,
        activities: [refund],
        refundGross: refund.refundGross,
        refundNet: refund.refundNet,
        saleGross: refund.saleGross,
        saleNet: refund.saleNet,
        referencedPaymentId: refund.referencedPaymentId,
      });
    }
  }
  const refundByOrderId = new Map<number, PaypalRefundGroup>(
    Array.from(refundGroupsMap.values())
      .filter((group) => group.orderId !== null)
      .map((group) => [normalizeOrderId(group.orderId), group])
  );
  const linkedOrderIds = new Set(payment.linkedOrders.map((o) => normalizeOrderId(o.id)));
  const refundOnlyGroups = Array.from(refundGroupsMap.values())
    .filter((group) => group.orderId === null || !linkedOrderIds.has(group.orderId));

  // Guard for test fixtures / pre-migration payloads.
  const vascoLinks = payment.vascoLinks || [];
  // Vasco documents this payment reaches through its linked orders. A document
  // that is also linked to the payment directly is dropped here so it shows up
  // once, in the direct list. These never enter the reconciliation math — the
  // order link already carries their value.
  const orderVascoDocuments = (payment.orderVascoDocuments || []).filter(
    (doc) => !vascoLinks.some((l) => l.invoiceNumber === doc.invoiceNumber)
  );
  const feeAdjustments = braintreeSummary?.feeAdjustments || [];
  const linkedTotalClass = paypalSummary
    ? 'text-gray-900'
    : isExact ? 'text-green-700' : isOverpaid ? 'text-orange-700' : isUnderpaid ? 'text-yellow-700' : 'text-gray-900';

  const mismatchCount = orderMismatchByOrderId.size;
  const refundsOnlyCount = refundOnlyGroups.length;
  // Unified unlinkedSettlements list mixes Revolut + Braintree rows. Each
  // source contributes only when its summary is present, so they never
  // overlap in practice.
  const unlinkedSettlements: UnlinkedSettlement[] = [
    ...(revolutSummary?.unlinkedSettlements || []).map((r) => ({
      txnId: r.revolutTxnId,
      orderId: r.orderId,
      orderNumber: r.orderNumber,
      orderAssignedByHand: r.orderAssignedByHand === true,
      currency: r.currency,
      gross: r.revolutGross,
      net: r.settlementAmount,
      wooTotal: r.wooTotal,
      orderTotalMismatch: r.orderTotalMismatch,
      reason: r.reason,
      linkedElsewherePaymentId: r.linkedElsewherePaymentId,
      sourceLabel: 'Revolut' as const,
      customerName: r.customerName,
      customerEmail: r.customerEmail,
      orderDescription: r.orderDescription,
      orderChannel: r.orderChannel,
      externalOrderId: r.externalOrderId,
      forceClosedAt: r.forceClosedAt,
      forceCloseNote: r.forceCloseNote,
      forceClosedByName: r.forceClosedByName,
    })),
    ...(braintreeSummary?.unlinkedSettlements || []).map((r) => ({
      txnId: r.braintreeTxnId,
      orderId: r.orderId,
      orderNumber: r.orderNumber,
      orderAssignedByHand: false,
      currency: r.currency,
      gross: r.braintreeGross,
      net: r.net,
      wooTotal: r.wooTotal,
      orderTotalMismatch: r.orderTotalMismatch,
      reason: r.reason,
      linkedElsewherePaymentId: r.linkedElsewherePaymentId,
      sourceLabel: 'Braintree' as const,
      customerName: null,
      customerEmail: null,
      orderDescription: null,
      orderChannel: null,
      externalOrderId: null,
      forceClosedAt: r.forceClosedAt,
      forceCloseNote: r.forceCloseNote,
      forceClosedByName: r.forceClosedByName,
    })),
  ];
  const unlinkedSettlementsCount = unlinkedSettlements.length;
  // Force-closed settlements (SW-2489) are resolved — the warning banner only
  // counts the ones still needing attention.
  const unresolvedSettlements = unlinkedSettlements.filter((u) => !u.forceClosedAt);
  const unresolvedSettlementsCount = unresolvedSettlements.length;
  const unresolvedSettlementsNet = parseFloat(
    unresolvedSettlements.reduce((acc, u) => acc + (u.net ?? u.gross), 0).toFixed(2)
  );
  const refundFundingActivities = revolutSummary?.refundFundingActivities || [];
  const refundFundingCount = refundFundingActivities.length;

  // Linked Orders cards render grouped by state: red rows (needing attention)
  // first, orange rows (informational or resolved) next, green rows last.
  const linkedOrderCardState = (order: LinkedOrder): 'red' | 'orange' | 'green' => {
    if (order.forceClosedAt != null) return 'orange';
    // Same precedence as `rowState` in renderLinkedOrderRow below. The two
    // ladders are separate and must stay in step, or a card is sorted into one
    // group and painted the colour of another.
    if ((order.settlementCount ?? 0) > 1) return 'red';
    if (refundByOrderId.has(normalizeOrderId(order.id))) return 'orange';
    const mismatch = orderMismatchByOrderId.get(normalizeOrderId(order.id));
    if (mismatch) return isBraintree || isRevolut ? 'orange' : 'red';
    return 'green';
  };
  const redLinkedOrders = payment.linkedOrders.filter((o) => linkedOrderCardState(o) === 'red');
  const orangeLinkedOrders = payment.linkedOrders.filter((o) => linkedOrderCardState(o) === 'orange');
  const greenLinkedOrders = payment.linkedOrders.filter((o) => linkedOrderCardState(o) === 'green');
  const closedUnlinkedSettlements = unlinkedSettlements.filter((u) => u.forceClosedAt);

  const vascoNumberValid = VASCO_NUMBER_RE.test(vascoNumberInput.trim());
  // Narrowed once here so the claims map below can use it without `!`.
  const vascoLookupInvoice = vascoLookup?.found ? vascoLookup.invoice ?? null : null;
  // Amount is mandatory for non-EUR payments (server 422) and for numbers not
  // found in the Vasco import (server 400).
  const vascoAmountRequired =
    payment.currency !== 'EUR' || (vascoLookup !== null && !vascoLookup.found);
  const vascoAddDisabled =
    !vascoNumberValid
    || vascoLinking
    || isForceClosed
    || Boolean(vascoLookup?.alreadyLinkedToThisPayment)
    || (vascoAmountRequired && vascoAmountInput.trim() === '');

  // Inline resolution form for item-level force close (SW-2489). Rendered
  // inside the row being closed so the accountant keeps the item's numbers in
  // view while writing the note.
  const renderItemForceCloseForm = () => (
    <div className="mt-2 pl-7 max-w-xl" data-testid="item-force-close-form">
      <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">
        Resolution (required)
      </label>
      <Textarea
        value={itemForceCloseNote}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setItemForceCloseNote(e.target.value)}
        placeholder="e.g. Remainder returned to the customer by bank transfer — nothing left to reconcile."
        rows={2}
        disabled={itemForceClosing}
      />
      {itemForceCloseError && (
        <div className="mt-1 flex items-start text-xs text-red-600">
          <AlertTriangle className="h-3.5 w-3.5 mr-1 flex-shrink-0 mt-0.5" />
          <span>{itemForceCloseError}</span>
        </div>
      )}
      <div className="mt-2 flex justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          icon={undefined}
          onClick={() => { setItemForceCloseTarget(null); setItemForceCloseNote(''); setItemForceCloseError(''); }}
          disabled={itemForceClosing}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          size="sm"
          icon={Lock}
          onClick={handleItemForceClose}
          disabled={!itemForceCloseNote.trim() || itemForceClosing}
        >
          {itemForceClosing ? 'Closing…' : 'Force close'}
        </Button>
      </div>
    </div>
  );

  // Row renderers for the Linked Orders card, extracted from the JSX below so
  // the list can render the rows grouped by state instead of by source array.
  const renderLinkedOrderRow = (order: LinkedOrder) => {
    const gross = parseFloat(order.total);
    const partial = order.partialAmount != null ? parseFloat(order.partialAmount) : null;
    const isPartial = partial !== null && partial < gross;
    const fee   = order.processingFee !== null ? parseFloat(order.processingFee) : null;
    const settlementNet = order.settlementAmount !== null ? parseFloat(order.settlementAmount) : null;
    const net   = settlementNet !== null
      ? settlementNet
      : fee !== null ? (partial ?? gross) - fee : null;
    const mismatch = orderMismatchByOrderId.get(normalizeOrderId(order.id));
    const refund = refundByOrderId.get(normalizeOrderId(order.id));
    const saleGross = refund?.saleGross ?? gross;
    const refundGrossAbs = Math.abs(refund?.refundGross ?? 0);
    const refundedGross = refund ? saleGross + refund.refundGross : null;
    const refundedNet = refund && refund.saleNet !== null
      ? refund.saleNet + refund.refundNet
      : null;
    const refundLabel = refundedGross !== null && Math.abs(refundedGross) <= 0.01 ? 'Full refund' : 'Partial refund';
    const orderCreditNotes = order.creditNotes || [];
    const creditNotesTotal = orderCreditNotes.reduce((s, cn) => s + cn.amount, 0);
    // Item-level force close (SW-2489): a resolved item renders
    // amber with its note regardless of what made it red before.
    const itemClosed = order.forceClosedAt != null;
    // The customer was charged more than once for this order inside this payout.
    // The link row carries the sum of all of them, so the amount on the right is
    // a multiple of the order total and the difference is money we may owe back.
    // Each individual charge matches the order total, so nothing else on this
    // card would flag it — without its own state it renders green (SW-2496).
    const settlementCount = order.settlementCount ?? 0;
    const multiSettlement = settlementCount > 1;
    // What the customer's card was actually charged, which is what we may owe
    // back. settlementAmount is NET of Revolut's fee and order.total is GROSS,
    // so subtracting one from the other compares two different things and
    // understates the refund by the whole fee sum. Add the fee back first:
    // accountingHelpers defines fee = |original| - |settlement|, so
    // settlement + fee is the original amount charged.
    const chargedGross = multiSettlement && settlementNet !== null
      ? parseFloat((settlementNet + (fee ?? 0)).toFixed(2))
      : null;
    // What is still owed back, not what was overcharged in the first place. A
    // refund already issued for this order has left our account, so counting it
    // again would tell the accountant to return the same money twice. Sources
    // disagree on the sign of refundGross (Revolut and Braintree send it
    // positive, PayPal negative), so use the absolute value the display line
    // above already relies on.
    const overcharged = chargedGross !== null
      ? parseFloat((chargedGross - gross - refundGrossAbs).toFixed(2))
      : null;
    const rowState = itemClosed
      ? 'forceClosed'
      : multiSettlement ? 'multiSettlement'
      : refund ? 'refund'
      : mismatch ? 'mismatch'
      : 'ok';
    const mismatchUsesOrange = Boolean(mismatch && (isBraintree || isRevolut));
    // Only red items get the Force close affordance — orange rows
    // are already informational and green rows are reconciled. A duplicate
    // charge is red and needs that affordance too: force close is the only way
    // to record "we refunded this outside the statement" and close the payout.
    const isRedRow = rowState === 'multiSettlement'
      || (rowState === 'mismatch' && !mismatchUsesOrange);
    const isForceCloseFormOpen = itemForceCloseTarget?.kind === 'link'
      && itemForceCloseTarget.orderId === order.id;
    // Split charge: one Revolut charge paid this order and others. The order
    // the statement names keeps the fee; each split order carries its own total
    // as settlementAmount, with no fee (syncRevolutLinkAmounts on the server).
    const isSplitPaid = order.splitChargeOrderId != null;
    const splitOrders = payment.linkedOrders.filter((o) => o.splitChargeOrderId === order.id);
    const splitShareOf = (o: LinkedOrder) => (o.settlementAmount != null
      ? parseFloat(o.settlementAmount)
      : o.partialAmount != null ? parseFloat(o.partialAmount) : parseFloat(o.total));
    const ownChargeShare = settlementNet !== null ? settlementNet + (fee ?? 0) : null;
    const chargeGross = splitOrders.length > 0 && ownChargeShare !== null
      ? ownChargeShare + splitOrders.reduce((acc, o) => acc + splitShareOf(o), 0)
      : null;
    const canSplit = isRevolut && !isForceClosed && !itemClosed && !isSplitPaid
      && settlementCount >= 1 && (Boolean(mismatch) || splitOrders.length > 0);
    const isSplitFormOpen = splitTargetOrderId === order.id;
    const rowClass = rowState === 'forceClosed' || rowState === 'refund'
      ? 'bg-orange-50 border-orange-200'
      : rowState === 'multiSettlement'
      ? 'bg-red-50 border-red-200'
      : rowState === 'mismatch'
      ? mismatchUsesOrange
        ? 'bg-orange-50 border-orange-200'
        : 'bg-red-50 border-red-200'
      : 'bg-green-50 border-green-200';
    return (
      <div
        key={order.id}
        data-testid={`linked-order-${order.orderNumber}`}
        className={`flex ${rowState === 'mismatch' || rowState === 'forceClosed' || rowState === 'multiSettlement' ? 'items-start' : 'items-center'} justify-between py-2 px-3 border rounded-md ${rowClass}`}
      >
        <div className="min-w-0 flex-1">
          {/* flex-wrap: every chip is flex-shrink-0, so without it a chip-heavy
              row overflows the column and the amount paints on top of it. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            {rowState === 'forceClosed' ? (
              <Lock className="h-4 w-4 text-orange-600 flex-shrink-0" />
            ) : rowState === 'multiSettlement' ? (
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
            ) : rowState === 'refund' ? (
              <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
            ) : rowState === 'mismatch' ? (
              <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${mismatchUsesOrange ? 'text-orange-600' : 'text-red-600'}`} />
            ) : (
              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
            )}
            <a
              href={getWooCommerceOrderUrl(order.wooOrderId)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              #{order.orderNumber}
            </a>
            <GkRowMark
              exceptions={gkExceptionsFor(gkData?.gk, {
                orderNumber: order.orderNumber,
                txnIds: (refund?.activities || []).map((a) => a.refundTxnId).filter(Boolean),
              })}
            />
            <span className="text-sm text-gray-600 truncate">{order.customerName}</span>
            {order.vascoInvoice && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 flex-shrink-0">
                Vasco {order.vascoInvoice}
              </span>
            )}
            {itemClosed && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 flex-shrink-0">
                Force closed
              </span>
            )}
            {multiSettlement && (
              // The count stays visible after a force close — it is still true —
              // but takes the resolved tone, so a closed item carries no red.
              <span
                data-testid={`multi-settlement-badge-${order.orderNumber}`}
                className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                  itemClosed ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                }`}
              >
                Paid {settlementCount}×
              </span>
            )}
            {isPartial && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 flex-shrink-0">
                Partial payment
              </span>
            )}
            {(isSplitPaid || splitOrders.length > 0) && (
              <span
                data-testid={`split-charge-badge-${order.orderNumber}`}
                className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 flex-shrink-0"
              >
                {isSplitPaid
                  ? `Split charge · paid by #${order.splitChargeOrderNumber ?? '?'}`
                  : `Split charge · also pays ${splitOrders.map((o) => `#${o.orderNumber}`).join(', ')}`}
              </span>
            )}
            {/* Hidden on multi-payment cards: their summary line already states
                charged total vs order total, so this chip repeats it. */}
            {mismatch && !multiSettlement && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 flex-shrink-0">
                {isBraintree ? 'Woo ≠ AIB' : 'Order total mismatch'}
              </span>
            )}
            {mismatch?.orderChannel && !multiSettlement && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 flex-shrink-0">
                {mismatch.orderChannel}
              </span>
            )}
          </div>
          {orderCreditNotes.length > 0 && (
            <div className="mt-0.5 pl-7 text-xs text-gray-700 tabular-nums">
              {orderCreditNotes.map((cn) => (
                <div key={cn.number}>
                  Credit note&nbsp;{cn.number}:&nbsp;{order.currency}&nbsp;{cn.amount.toFixed(2)}
                  {!creditApplied && (
                    <span className="text-gray-500">&nbsp;(separate refund — not applied)</span>
                  )}
                </div>
              ))}
              {creditApplied && (
                <div className="text-gray-500">
                  {order.currency}&nbsp;{gross.toFixed(2)}
                  &nbsp;−&nbsp;credit&nbsp;{order.currency}&nbsp;{Math.abs(creditNotesTotal).toFixed(2)}
                  &nbsp;=&nbsp;{order.currency}&nbsp;{(gross + creditNotesTotal).toFixed(2)}
                </div>
              )}
            </div>
          )}
          {/* One line, only while the item is open — a force-closed row is
              resolved. Gated on a present settlement amount so no figure here
              is ever invented. Net received and fee live in the amount column. */}
          {rowState === 'multiSettlement' && settlementNet !== null && (
            <div className="mt-0.5 pl-7 text-xs text-red-700 tabular-nums">
              Customer paid {order.currency} {(chargedGross ?? 0).toFixed(2)} in {settlementCount} charges
              for a {order.currency} {gross.toFixed(2)} order
              {overcharged !== null && overcharged > 0.01 && (
                <>
                  <span className="text-red-500"> · </span>
                  <span className="font-medium">
                    still to refund {order.currency} {overcharged.toFixed(2)}
                    {refund && ' after the refund below'}
                  </span>
                </>
              )}
            </div>
          )}
          {refund && (
            <div className="mt-0.5 pl-7 text-xs text-orange-700 tabular-nums">
              {refundLabel}: {txSourceLabel || 'Statement'} {refund.currency} {saleGross.toFixed(2)}
              &nbsp;− refund&nbsp;{refund.currency}&nbsp;{refundGrossAbs.toFixed(2)}
              &nbsp;=&nbsp;gross&nbsp;{refund.currency}&nbsp;{(refundedGross ?? 0).toFixed(2)}
              {refund.activities.length > 1 && (
                <>
                  <span className="text-orange-500">&nbsp;·&nbsp;</span>
                  {refund.activities.length} refunds
                </>
              )}
              {refundedNet !== null && (
                <>
                  <span className="text-orange-500">&nbsp;·&nbsp;</span>
                  net impact&nbsp;{refund.currency}&nbsp;{refundedNet.toFixed(2)}
                </>
              )}
            </div>
          )}
          {/* Skipped on multi-payment rows: their one-line summary already
              states charged total vs order total, and this block compares a
              single transaction, which reads as a contradiction next to it. */}
          {!refund && mismatch && !multiSettlement && (
            <>
              {isBraintree && settlementNet !== null ? (
                <div className="mt-0.5 pl-7 text-xs text-orange-700 tabular-nums leading-snug">
                  Woo {order.currency}{' '}
                  {mismatch.wooTotal !== null ? mismatch.wooTotal.toFixed(2) : gross.toFixed(2)}
                  {' '}vs AIB gross {mismatch.txnGross.toFixed(2)}
                  {mismatch.delta != null && Math.abs(mismatch.delta) > 0.001 && (
                    <> (Δ {Math.abs(mismatch.delta).toFixed(2)})</>
                  )}
                  <span className="text-orange-500"> · </span>
                  payout uses AIB net {order.currency} {settlementNet.toFixed(2)}
                </div>
              ) : (
                <div className={`mt-0.5 pl-7 text-xs tabular-nums ${mismatchUsesOrange ? 'text-orange-700' : 'text-red-700'}`}>
                  {mismatch.sourceLabel} {mismatch.currency} {mismatch.txnGross.toFixed(2)}
                  &nbsp;vs Woo&nbsp;{order.currency}&nbsp;
                  {mismatch.wooTotal !== null ? mismatch.wooTotal.toFixed(2) : '—'}
                  {mismatch.delta != null && Math.abs(mismatch.delta) > 0.001 && (
                    <>
                      <span className="text-orange-500">&nbsp;·&nbsp;</span>
                      Δ&nbsp;{mismatch.currency}&nbsp;{Math.abs(mismatch.delta).toFixed(2)}
                    </>
                  )}
                </div>
              )}
              {(mismatch.customerName || mismatch.customerEmail) && !isBraintree && (
                <div className="mt-0.5 pl-7 text-xs text-gray-700">
                  {mismatch.customerName}
                  {mismatch.customerName && mismatch.customerEmail && <span className="text-gray-400"> · </span>}
                  {mismatch.customerEmail && <span className="text-gray-500">{mismatch.customerEmail}</span>}
                </div>
              )}
              {mismatch.orderDescription && !isBraintree && (
                <div className="mt-0.5 pl-7 text-xs">
                  <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mr-1">Remittance info</span>
                  <span className="text-gray-800">{mismatch.orderDescription}</span>
                </div>
              )}
            </>
          )}
          {chargeGross !== null && ownChargeShare !== null && (
            <div className="mt-0.5 pl-7 text-xs text-indigo-800 tabular-nums" data-testid={`split-charge-summary-${order.orderNumber}`}>
              Revolut charge {order.currency} {chargeGross.toFixed(2)}
              &nbsp;=&nbsp;#{order.orderNumber} {order.currency} {ownChargeShare.toFixed(2)}
              {splitOrders.map((o) => (
                <span key={o.id}>&nbsp;+&nbsp;#{o.orderNumber} {o.currency} {splitShareOf(o).toFixed(2)}</span>
              ))}
              {fee !== null && (
                <>
                  <span className="text-indigo-400"> · </span>
                  fee {order.currency} {fee.toFixed(2)} stays on #{order.orderNumber}
                </>
              )}
            </div>
          )}
          {isSplitPaid && (
            <div className="mt-0.5 pl-7 text-xs" data-testid={`split-charge-note-${order.orderNumber}`}>
              <p className="text-indigo-800">
                Paid by the Revolut charge of order #{order.splitChargeOrderNumber ?? '?'}. No fee on this order.
              </p>
              {order.splitNote && <p className="whitespace-pre-wrap text-gray-700">{order.splitNote}</p>}
              <p className="text-gray-500">
                Split by {order.splitByName || 'Unknown'}
                {order.splitAt ? ` · ${formatDateTime(order.splitAt)}` : ''}
              </p>
            </div>
          )}
          {isSplitFormOpen && (
            <div className="mt-2 pl-7 max-w-xl" data-testid="split-charge-form">
              <p className="text-xs text-gray-700 mb-2">
                Which other order did this Revolut charge pay? That order gets its full total from
                this charge. The fee stays on #{order.orderNumber}.
                {mismatch && mismatch.wooTotal !== null && mismatch.txnGross - mismatch.wooTotal > 0.02 && (
                  <> Not yet matched to an order: {mismatch.currency} {(mismatch.txnGross - mismatch.wooTotal).toFixed(2)}.</>
                )}
              </p>
              <label htmlFor={`split-order-${order.id}`} className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                Order number
              </label>
              <input
                id={`split-order-${order.id}`}
                type="text"
                value={splitOrderInput}
                onChange={(e) => { setSplitOrderInput(e.target.value); if (splitError) setSplitError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSplitCharge(); }}
                placeholder="e.g. 150597"
                disabled={splitting}
                className="w-48 h-9 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent disabled:bg-gray-50"
              />
              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mt-2 mb-1">
                Note (optional)
              </label>
              <Textarea
                value={splitNoteInput}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSplitNoteInput(e.target.value)}
                placeholder="e.g. Split invoice, the customer paid both orders in one charge."
                rows={2}
                disabled={splitting}
              />
              {splitError && (
                <div className="mt-1 flex items-start text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1 flex-shrink-0 mt-0.5" />
                  <span>{splitError}</span>
                </div>
              )}
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={undefined}
                  onClick={() => setSplitTargetOrderId(null)}
                  disabled={splitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={Link2}
                  onClick={handleSplitCharge}
                  disabled={!splitOrderInput.trim() || splitting}
                >
                  {splitting ? 'Saving…' : 'Split charge'}
                </Button>
              </div>
            </div>
          )}
          {itemClosed && (
            <div className="mt-1 pl-7 text-xs" data-testid={`force-close-note-${order.orderNumber}`}>
              <p className="whitespace-pre-wrap text-gray-700">{order.forceCloseNote}</p>
              <p className="mt-0.5 text-gray-500">
                Force closed by {order.forceClosedByName || 'Unknown'}
                {order.forceClosedAt ? ` · ${formatDateTime(order.forceClosedAt)}` : ''}
              </p>
            </div>
          )}
          {isForceCloseFormOpen && renderItemForceCloseForm()}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
          {/* Ordered to match `rowState` above: multi-settlement outranks refund.
              The refund branch derives its figures from a SINGLE sale row, so on
              a repeatedly charged order it would headline one charge out of many
              while the card is red about all of them. */}
          {multiSettlement && settlementNet !== null ? (
            <div className="text-right tabular-nums">
              <div className={`text-sm font-medium ${itemClosed ? 'text-orange-700' : 'text-red-700'}`}>
                {order.currency} {settlementNet.toFixed(2)}
              </div>
              {fee !== null && (
                <div className={`text-xs ${itemClosed ? 'text-orange-600' : 'text-red-600'}`}>
                  net of {order.currency} {fee.toFixed(2)} fees
                </div>
              )}
            </div>
          ) : multiSettlement ? (
            // Charged several times but the link carries no settlement amount.
            // The fallback branch would print the order total here with no
            // label, next to a red "Paid N×" badge — reading as if that is what
            // we received. Print the same figure, but say what it is.
            <div className="text-right tabular-nums">
              <div className={`text-sm font-medium ${itemClosed ? 'text-orange-700' : 'text-red-700'}`}>
                {order.currency} {gross.toFixed(2)}
              </div>
              <div className={`text-xs ${itemClosed ? 'text-orange-600' : 'text-red-600'}`}>
                order total — settled amount unknown
              </div>
            </div>
          ) : refund ? (
            <div className="text-right tabular-nums">
              <div className="text-xs text-gray-400 line-through">
                {order.currency} {gross.toFixed(2)}
              </div>
              <div className="text-sm font-medium text-orange-700">
                {refund.currency} {(refundedGross ?? 0).toFixed(2)}
              </div>
            </div>
          ) : settlementNet !== null && (isRevolut || isBraintree) ? (
            <div className="text-right tabular-nums">
              <div className={`text-sm font-medium ${mismatch ? 'text-orange-700' : 'text-gray-900'}`}>
                {order.currency} {settlementNet.toFixed(2)}
              </div>
              {!(mismatch && isBraintree) && (
                <div className="text-xs text-gray-400">
                  {mismatch
                    ? <>order total {order.currency} {gross.toFixed(2)} ≠ Revolut gross</>
                    : isSplitPaid
                      ? <>share of the #{order.splitChargeOrderNumber ?? '?'} charge</>
                    : isBraintree
                      ? <>AIB net on this payout</>
                      : <>net of {order.currency} {fee !== null ? fee.toFixed(2) : '0.00'} fee</>}
                </div>
              )}
            </div>
          ) : fee !== null && net !== null ? (
            <div className="text-right tabular-nums">
              <div className="text-sm font-medium text-gray-900">
                {order.currency} {(partial ?? gross).toFixed(2)}
                {isPartial && <span className="text-xs font-normal text-gray-400"> / {gross.toFixed(2)}</span>}
              </div>
              <div className="text-xs text-gray-400">
                − fee {order.currency} {fee.toFixed(2)}
                &nbsp;=&nbsp;
                <span className="text-gray-600">{order.currency} {net.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="text-right tabular-nums">
              <span className="text-sm font-medium text-gray-900">
                {order.currency} {(partial ?? gross).toFixed(2)}
              </span>
              {isPartial && (
                <div className="text-xs text-gray-400">of {order.currency} {gross.toFixed(2)}</div>
              )}
            </div>
          )}
          {itemClosed ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleItemReopen({ kind: 'link', orderId: order.id })}
              disabled={reopeningItemKey === `link-${order.id}`}
              icon={RotateCcw}
            >
              Reopen
            </Button>
          ) : (
            <>
              {canSplit && !isSplitFormOpen && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => openSplitForm(order.id)}
                  icon={Link2}
                >
                  Split charge
                </Button>
              )}
              {isSplitPaid && !isForceClosed && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleUndoSplit(order.id)}
                  disabled={undoingSplitOrderId === order.id}
                  icon={RotateCcw}
                >
                  Undo split
                </Button>
              )}
              {isRedRow && !isForceClosed && !isForceCloseFormOpen && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => openItemForceCloseForm({ kind: 'link', orderId: order.id })}
                  icon={Lock}
                >
                  Force close
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleRemoveOrder(order.id)}
                disabled={removingId === order.id}
                icon={X}
              >
                Remove
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderUnlinkedSettlementRow = (u: UnlinkedSettlement) => {
    const reasonLabel =
      u.reason === 'order_not_found' ? 'Order not in database'
      : u.reason === 'linked_elsewhere' ? `Already linked to payment #${u.linkedElsewherePaymentId ?? '?'}`
      : u.reason === 'currency_mismatch' ? 'Currency mismatch'
      : 'Not linked';
    const sourceWord = u.sourceLabel === 'Revolut' ? 'Revolut' : 'AIB';
    // Item-level force close (SW-2489): resolved settlements
    // render amber with their note instead of red.
    const settlementClosed = u.forceClosedAt != null;
    const settlementSource = u.sourceLabel === 'Revolut' ? 'revolut' as const : 'braintree' as const;
    const isSettlementFormOpen = itemForceCloseTarget?.kind === 'settlement'
      && itemForceCloseTarget.txnId === u.txnId;
    // POS / card-reader and other unresolved Revolut settlements
    // carry no order reference, so let the user assign one here.
    // An order assigned by hand that has no link on this payment (it is linked
    // to another payment, or its link is gone). assign-order refuses a
    // settlement that already has an order, so the order is removed first;
    // that brings the Assign field back.
    const canRemoveOrder = u.sourceLabel === 'Revolut'
      && u.orderAssignedByHand
      && !settlementClosed
      && !isForceClosed;
    const canAssignOrder = u.sourceLabel === 'Revolut'
      && (u.reason === 'order_not_found' || u.reason === 'unlinked')
      && !settlementClosed
      && !canRemoveOrder;
    const assignValue = assignInputs[u.txnId] || '';
    return (
      <div
        key={`unlinked-${u.txnId}`}
        data-testid={`unlinked-settlement-${u.orderNumber || u.txnId}`}
        className={`flex items-start justify-between py-2 px-3 border rounded-md ${
          settlementClosed ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 min-w-0 flex-wrap">
            {settlementClosed ? (
              <Lock className="h-4 w-4 text-orange-600 flex-shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
            )}
            <span className="font-medium text-sm text-gray-800">
              {u.orderNumber ? `#${u.orderNumber}` : u.txnId}
            </span>
            <GkRowMark exceptions={gkExceptionsFor(gkData?.gk, { orderNumber: u.orderNumber, txnIds: [u.txnId] })} />
            {settlementClosed ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 flex-shrink-0">
                Force closed
              </span>
            ) : (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-800 flex-shrink-0">
                Settlement not linked
              </span>
            )}
            {u.orderChannel && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 flex-shrink-0">
                {u.orderChannel}
              </span>
            )}
            {u.reason === 'linked_elsewhere' && u.linkedElsewherePaymentId && (
              <button
                type="button"
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                onClick={() => navigate(`/accounting/payments/${u.linkedElsewherePaymentId}`)}
              >
                View payment #{u.linkedElsewherePaymentId}
              </button>
            )}
          </div>
          <div className={`mt-0.5 pl-7 text-xs tabular-nums ${settlementClosed ? 'text-orange-700' : 'text-red-700'}`}>
            {reasonLabel}. {sourceWord} {u.currency} {u.gross.toFixed(2)}
            {u.net !== null && u.net !== u.gross && (
              <>&nbsp;·&nbsp;net&nbsp;{u.currency}&nbsp;{u.net.toFixed(2)}</>
            )}
            {u.orderTotalMismatch && u.wooTotal !== null && (
              <>&nbsp;·&nbsp;Woo&nbsp;{u.currency}&nbsp;{u.wooTotal.toFixed(2)}</>
            )}
          </div>
          {(u.customerName || u.customerEmail) && (
            <div className="mt-0.5 pl-7 text-xs text-gray-700">
              {u.customerName}
              {u.customerName && u.customerEmail && <span className="text-gray-400"> · </span>}
              {u.customerEmail && <span className="text-gray-500">{u.customerEmail}</span>}
            </div>
          )}
          {u.orderDescription && (
            <div className="mt-0.5 pl-7 text-xs">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mr-1">Remittance info</span>
              <span className="text-gray-800">{u.orderDescription}</span>
            </div>
          )}
          {u.externalOrderId && (
            <div className="mt-0.5 pl-7 text-xs text-gray-500">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mr-1">Revolut order ID</span>
              <span className="font-mono text-gray-700">{u.externalOrderId}</span>
            </div>
          )}
          {canAssignOrder && (
            <div className="mt-2 pl-7">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={assignValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAssignInputs((prev) => ({ ...prev, [u.txnId]: v }));
                    if (assignError[u.txnId]) setAssignError((prev) => ({ ...prev, [u.txnId]: '' }));
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAssignOrder(u.txnId); }}
                  placeholder="Assign order #…"
                  disabled={assigningTxnId === u.txnId}
                  className="h-8 px-2 py-1 w-40 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent disabled:bg-gray-50"
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleAssignOrder(u.txnId)}
                  disabled={!assignValue.trim() || assigningTxnId === u.txnId}
                  icon={Link2}
                >
                  {assigningTxnId === u.txnId ? 'Assigning…' : 'Assign'}
                </Button>
              </div>
              {assignError[u.txnId] && (
                <div className="mt-1 flex items-start text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1 flex-shrink-0 mt-0.5" />
                  <span>{assignError[u.txnId]}</span>
                </div>
              )}
            </div>
          )}
          {canRemoveOrder && (
            <div className="mt-2 pl-7">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleRemoveAssignedOrder(u.txnId)}
                disabled={removingOrderTxnId === u.txnId}
                icon={X}
              >
                {removingOrderTxnId === u.txnId ? 'Removing…' : 'Remove order'}
              </Button>
              {assignError[u.txnId] && (
                <div className="mt-1 flex items-start text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1 flex-shrink-0 mt-0.5" />
                  <span>{assignError[u.txnId]}</span>
                </div>
              )}
            </div>
          )}
          {settlementClosed && (
            <div className="mt-1 pl-7 text-xs" data-testid={`force-close-note-settlement-${u.txnId}`}>
              <p className="whitespace-pre-wrap text-gray-700">{u.forceCloseNote}</p>
              <p className="mt-0.5 text-gray-500">
                Force closed by {u.forceClosedByName || 'Unknown'}
                {u.forceClosedAt ? ` · ${formatDateTime(u.forceClosedAt)}` : ''}
              </p>
            </div>
          )}
          {isSettlementFormOpen && renderItemForceCloseForm()}
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0 ml-2">
          <div className="text-right tabular-nums">
            <div className={`text-sm font-medium ${settlementClosed ? 'text-orange-700' : 'text-red-700'}`}>
              {u.currency} {(u.net ?? u.gross).toFixed(2)}
            </div>
            <div className="text-xs text-gray-400">
              {sourceWord} deposit {settlementClosed ? '(force closed)' : '(no link)'}
            </div>
          </div>
          {settlementClosed ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleItemReopen({ kind: 'settlement', source: settlementSource, txnId: u.txnId })}
              disabled={reopeningItemKey === `settlement-${u.txnId}`}
              icon={RotateCcw}
            >
              Reopen
            </Button>
          ) : !isForceClosed && !isSettlementFormOpen && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => openItemForceCloseForm({ kind: 'settlement', source: settlementSource, txnId: u.txnId })}
              icon={Lock}
            >
              Force close
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title={`Payment from ${payment.counterparty || 'Unknown counterparty'}`}
        subtitle={`Payment ID ${payment.acctSvcrRef}`}
        onBack={() => navigate('/accounting')}
        reserveWidthPx={0}
        actions={<></>}
      />

      {error && (
        <AlertMessage type="error" message={error} title="" onDismiss={() => setError('')} dismissible>
          {''}
        </AlertMessage>
      )}
      {success && (
        <AlertMessage type="success" message={success} title="" onDismiss={() => setSuccess('')} dismissible>
          {''}
        </AlertMessage>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - payment details */}
        <div className="lg:col-span-2 space-y-6">
        {/* Payment metadata card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {formatDateTime(payment.date)}
                </h3>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-700">
                  {payment.currency} {payment.amount.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">
            {payment.remittanceInfo && (
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Remittance info</span>
                <p className="text-sm text-gray-800 mt-0.5">{payment.remittanceInfo}</p>
              </div>
            )}

            {/* Payment source selector */}
            <div>
              <label htmlFor="payment-source" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Payment source
              </label>
              <Select
                id="payment-source"
                value={payment.paymentSource || ''}
                onChange={handleSourceChange}
                options={PAYMENT_SOURCES}
                placeholder="Select source…"
                className="max-w-xs"
              >{null}</Select>
            </div>

            {/* Reconciliation indicator */}
            <div className="pt-2 space-y-2">
              {payment.reconciliationNote && !isBraintree && (
                <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{payment.reconciliationNote}</span>
                </div>
              )}

              {settlementFee > 0 && (
                <div className="text-sm text-gray-600">
                  Includes {payment.currency} {settlementFee.toFixed(2)} transfer fee deducted before the payout reached the bank.
                </div>
              )}

              {isItemForceClosed ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-teal-50 text-teal-700">
                  <Lock className="h-4 w-4" />
                  Completed (items force closed)
                </div>
              ) : isExact ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-700">
                  <CheckCircle className="h-4 w-4" />
                  Fully reconciled
                </div>
              ) : isOverpaid ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-orange-50 text-orange-700">
                  <AlertTriangle className="h-4 w-4" />
                  Overpaid by {payment.currency} {remaining.toFixed(2)}
                </div>
              ) : isUnderpaid ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-50 text-yellow-700">
                  <AlertTriangle className="h-4 w-4" />
                  Underpaid by {payment.currency} {Math.abs(remaining).toFixed(2)}
                </div>
              ) : null}

              {isForceClosed && (
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-teal-50 text-teal-700">
                    <Lock className="h-4 w-4" />
                    Completed (force closed)
                  </div>
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    <p className="whitespace-pre-wrap">{payment.forceCloseNote}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {payment.forceClosedByName || 'Unknown'}
                      {payment.forceClosedAt ? ` · ${formatDateTime(payment.forceClosedAt)}` : ''}
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" icon={RotateCcw} onClick={() => setShowReopenModal(true)}>
                    Reopen
                  </Button>
                </div>
              )}

              {canForceClose && (
                <div className="pt-1">
                  <Button variant="secondary" size="sm" icon={Lock} onClick={() => setShowForceCloseModal(true)}>
                    Force close
                  </Button>
                  <p className="mt-1 text-xs text-gray-500">
                    Resolve this payment if it can&apos;t be linked to an order (e.g. a refund). A note is required.
                  </p>
                </div>
              )}

              {txSummary ? (
                <div className="text-xs text-gray-500 tabular-nums">
                  {isRevolut || isBraintree ? 'Linked net' : 'Sales net'}&nbsp;{payment.currency}&nbsp;
                  {(isRevolut || isBraintree ? linkedNet : txSummary.salesNet).toFixed(2)}
                  {txSummary.refundCount > 0 && (
                    <>
                      &nbsp;−&nbsp;refunds&nbsp;{payment.currency}&nbsp;{Math.abs(txSummary.refundsNet).toFixed(2)}
                    </>
                  )}
                  {braintreeFeesNet !== 0 && (
                    <>
                      &nbsp;−&nbsp;AIB adjustments&nbsp;{payment.currency}&nbsp;{Math.abs(braintreeFeesNet).toFixed(2)}
                    </>
                  )}
                  {isRevolut && refundFundingNet !== 0 && (
                    <>
                      &nbsp;+&nbsp;refund-funding&nbsp;{payment.currency}&nbsp;{refundFundingNet.toFixed(2)}
                    </>
                  )}
                  {forcedSettlementsNet !== 0 && (
                    <>
                      &nbsp;{forcedSettlementsNet > 0 ? '+' : '−'}&nbsp;force-closed items&nbsp;{payment.currency}&nbsp;{Math.abs(forcedSettlementsNet).toFixed(2)}
                    </>
                  )}
                  {vascoLinkedNet !== 0 && (
                    <>
                      &nbsp;{vascoLinkedNet > 0 ? '+' : '−'}&nbsp;Vasco invoices&nbsp;{payment.currency}&nbsp;{Math.abs(vascoLinkedNet).toFixed(2)}
                    </>
                  )}
                  {creditApplied && (
                    <>
                      &nbsp;{creditNotesNet > 0 ? '+' : '−'}&nbsp;credit notes&nbsp;{payment.currency}&nbsp;{Math.abs(creditNotesNet).toFixed(2)}
                    </>
                  )}
                  {settlementFee !== 0 && (
                    <>
                      &nbsp;−&nbsp;transfer fee&nbsp;{payment.currency}&nbsp;{settlementFee.toFixed(2)}
                    </>
                  )}
                  {hasCarryover && carryoverIn !== 0 && (
                    <>
                      &nbsp;+&nbsp;carryover in&nbsp;{payment.currency}&nbsp;{carryoverIn.toFixed(2)}
                    </>
                  )}
                  {hasCarryover && carryoverOut !== 0 && (
                    <>
                      &nbsp;−&nbsp;carryover out&nbsp;{payment.currency}&nbsp;{carryoverOut.toFixed(2)}
                    </>
                  )}
                  &nbsp;=&nbsp;expected&nbsp;{payment.currency}&nbsp;{comparisonSum.toFixed(2)}
                  {(isRevolut || isBraintree) && (() => {
                    // The bank tie-out: with a complete statement, the sum of
                    // every transaction on the payout IS the bank credit. This
                    // holds regardless of how far order linking has come, so
                    // the accountant always sees what explains the amount.
                    const statementTotal = (txSummary.totalNet || 0) + carryoverIn - carryoverOut;
                    const diff = payment.amount - statementTotal;
                    return (
                      <div>
                        Statement total&nbsp;{payment.currency}&nbsp;{statementTotal.toFixed(2)}
                        &nbsp;across&nbsp;{txSummary.transactionCount}&nbsp;transactions
                        {Math.abs(diff) <= 0.02 ? (
                          <span className="text-green-700">&nbsp;— matches the payment amount</span>
                        ) : (
                          <span className="text-orange-700">
                            &nbsp;— differs from the payment by&nbsp;{payment.currency}&nbsp;{diff.toFixed(2)} (statement rows missing?)
                          </span>
                        )}
                        {openUnlinkedNet !== 0 && (
                          <>
                            ;&nbsp;open items&nbsp;{payment.currency}&nbsp;{openUnlinkedNet.toFixed(2)}&nbsp;await linking or force-close
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : hasFeesData && linkedFees > 0 && (
                <div className="text-xs text-gray-500 tabular-nums">
                  Gross&nbsp;{payment.currency}&nbsp;{linkedGross.toFixed(2)}
                  &nbsp;−&nbsp;fees&nbsp;{payment.currency}&nbsp;{linkedFees.toFixed(2)}
                  &nbsp;=&nbsp;net&nbsp;{payment.currency}&nbsp;{linkedNet.toFixed(2)}
                </div>
              )}

              {!txSummary && vascoLinkedNet !== 0 && (
                <div className="text-xs text-gray-500 tabular-nums">
                  +&nbsp;Vasco invoices&nbsp;{payment.currency}&nbsp;{vascoLinkedNet.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment-level discrepancy banner — Revolut and Braintree surface order-total mismatches, refund-only rows, and unlinked settlements */}
        {(isRevolut || isBraintree) && (mismatchCount > 0 || refundsOnlyCount > 0 || unresolvedSettlementsCount > 0) && (
          <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-orange-600" />
            <div>
              {mismatchCount > 0 && (
                <span>
                  {mismatchCount} order total mismatch{mismatchCount !== 1 ? 'es' : ''} vs {isRevolut ? 'Revolut' : 'AIB'} statement.
                </span>
              )}
              {mismatchCount > 0 && (refundsOnlyCount > 0 || unresolvedSettlementsCount > 0) && <span>&nbsp;·&nbsp;</span>}
              {unresolvedSettlementsCount > 0 && (
                <span>
                  {unresolvedSettlementsCount} unlinked settlement{unresolvedSettlementsCount !== 1 ? 's' : ''}
                  &nbsp;({payment.currency}&nbsp;{unresolvedSettlementsNet.toFixed(2)}) — see below.
                </span>
              )}
              {unresolvedSettlementsCount > 0 && refundsOnlyCount > 0 && <span>&nbsp;·&nbsp;</span>}
              {refundsOnlyCount > 0 && (
                <span>
                  {refundsOnlyCount} refund row{refundsOnlyCount !== 1 ? 's' : ''} for orders settled in another payout.
                </span>
              )}
              <span className="text-orange-700">&nbsp;{isRevolut ? 'Revolut' : 'AIB'} statement remains the source of truth.</span>
            </div>
          </div>
        )}

        {/* Standalone AIB adjustments — chargeback debits and chargeback handling
            fees with no card transaction behind them. Informational, not a
            discrepancy: the amount is already part of the expected payout total. */}
        {feeAdjustments.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                AIB Adjustments ({feeAdjustments.length})
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Chargeback debits and handling fees AIB deducted from this payout. They are not
                order settlements, so they create no link, and they are already included in the
                expected total.
              </p>
            </div>
            <ul className="divide-y divide-gray-100">
              {feeAdjustments.map((adj) => (
                <li key={adj.braintreeTxnId} className="px-6 py-3 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">
                        {adj.chargebackCaseNumber
                          ? `Chargeback case ${adj.chargebackCaseNumber}`
                          : 'Chargeback fee'}
                        {adj.sourceOrderNumber && (
                          <span className="ml-2 font-normal text-gray-600">
                            order #{adj.sourceOrderNumber}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 tabular-nums">
                        Processed {adj.processingDate}
                        {adj.chargebackAmount !== 0 && (
                          <>
                            &nbsp;·&nbsp;chargeback&nbsp;
                            {adj.chargebackDebitCurrency || adj.currency}&nbsp;
                            {adj.chargebackAmount.toFixed(2)}
                          </>
                        )}
                        {adj.chargebackFeeAmount !== 0 && (
                          <>
                            &nbsp;·&nbsp;fee&nbsp;
                            {adj.chargebackFeeCurrency || adj.currency}&nbsp;
                            {adj.chargebackFeeAmount.toFixed(2)}
                          </>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-400">
                        {adj.braintreeTxnId}
                        {adj.mid && <>&nbsp;·&nbsp;MID {adj.mid}</>}
                        {adj.merchantAccountToken && <>&nbsp;·&nbsp;{adj.merchantAccountToken}</>}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right tabular-nums font-medium text-gray-900">
                      {adj.currency}&nbsp;{adj.net.toFixed(2)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Vasco GK status of the payment, with Resolve and the decisions */}
        <PaymentGkBox paymentId={payment.id} data={gkData} error={gkError} onChanged={gkChanged} />

        {/* Linked orders card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Linked Orders</h3>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Order number input */}
            <div>
              <label htmlFor="order-number" className="block text-sm font-medium text-gray-700 mb-2">
                Link an order
              </label>
              <div className="flex gap-2">
                <input
                  id="order-number"
                  ref={orderInputRef}
                  type="text"
                  value={orderInput}
                  onChange={(e) => {
                    setOrderInput(e.target.value);
                    if (linkError) setLinkError('');
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Order number…"
                  disabled={linking}
                  className="flex-1 h-10 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent disabled:bg-gray-50"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={partialAmountInput}
                  onChange={(e) => setPartialAmountInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Partial amount (optional)"
                  disabled={linking}
                  title="Leave blank to apply the full order total to this payment"
                  className="w-44 h-10 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent disabled:bg-gray-50"
                />
                <Button
                  variant="primary"
                  onClick={handleLinkOrder}
                  disabled={!orderInput.trim() || linking}
                  icon={Plus}
                >
                  {linking ? 'Adding…' : 'Add'}
                </Button>
              </div>
              {linkError && (
                <div className="mt-2 flex items-start text-sm text-red-600">
                  <AlertTriangle className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                  <span>{linkError}</span>
                </div>
              )}
            </div>

            {/* Linked orders list */}
            {(payment.linkedOrders.length > 0 || refundOnlyGroups.length > 0 || unlinkedSettlementsCount > 0 || refundFundingCount > 0) && (
              <div className="space-y-2">
                {redLinkedOrders.map(renderLinkedOrderRow)}
                {unresolvedSettlements.map(renderUnlinkedSettlementRow)}
                {orangeLinkedOrders.map(renderLinkedOrderRow)}
                {closedUnlinkedSettlements.map(renderUnlinkedSettlementRow)}

                {refundOnlyGroups.map((refund) => {
                  const saleGross = refund.saleGross ?? refund.wooTotal ?? 0;
                  const refundGrossAbs = Math.abs(refund.refundGross);
                  const refundedGross = saleGross + refund.refundGross;
                  const refundedNet = refund.saleNet !== null ? refund.saleNet + refund.refundNet : null;
                  const refundLabel = Math.abs(refundedGross) <= 0.01 ? 'Full refund' : 'Partial refund';
                  const orderLabel = refund.orderNumber ? `#${refund.orderNumber}` : refund.activities[0]?.referenceTxnId || refund.activities[0]?.refundTxnId;
                  const refundSourceLabel = txSourceLabel === 'Braintree' ? 'AIB' : txSourceLabel || 'PayPal';

                  return (
                    <div
                      key={refund.key}
                      data-testid={`refund-order-${refund.orderNumber || refund.activities[0]?.refundTxnId}`}
                      className="flex items-center justify-between py-2 px-3 border rounded-md bg-orange-50 border-orange-200"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                          <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
                          {refund.wooOrderId ? (
                            <a
                              href={getWooCommerceOrderUrl(refund.wooOrderId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {orderLabel}
                            </a>
                          ) : (
                            <span className="font-medium text-sm text-gray-800">{orderLabel}</span>
                          )}
                          <GkRowMark
                            exceptions={gkExceptionsFor(gkData?.gk, {
                              orderNumber: refund.orderNumber,
                              txnIds: refund.activities.map((a) => a.refundTxnId).filter(Boolean),
                            })}
                          />
                          <span className="text-sm text-gray-600 truncate">
                            {refund.customerName || `${refundSourceLabel} refund`}
                          </span>
                        </div>
                        <div className="mt-0.5 pl-7 text-xs text-orange-700 tabular-nums">
                          {refundLabel}: {refundSourceLabel} {refund.currency} {saleGross.toFixed(2)}
                          &nbsp;− refund&nbsp;{refund.currency}&nbsp;{refundGrossAbs.toFixed(2)}
                          &nbsp;=&nbsp;gross&nbsp;{refund.currency}&nbsp;{refundedGross.toFixed(2)}
                          {refund.activities.length > 1 && (
                            <>
                              <span className="text-orange-500">&nbsp;·&nbsp;</span>
                              {refund.activities.length} refunds
                            </>
                          )}
                          {refundedNet !== null && (
                            <>
                              <span className="text-orange-500">&nbsp;·&nbsp;</span>
                              net impact&nbsp;{refund.currency}&nbsp;{refundedNet.toFixed(2)}
                            </>
                          )}
                        </div>
                        {refund.customerEmail && (
                          <div className="mt-0.5 pl-7 text-xs text-gray-700">
                            <span className="text-gray-500">{refund.customerEmail}</span>
                          </div>
                        )}
                        {refund.orderDescription && (
                          <div className="mt-0.5 pl-7 text-xs">
                            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mr-1">Remittance info</span>
                            <span className="text-gray-800">{refund.orderDescription}</span>
                          </div>
                        )}
                        {refund.referencedPaymentId && refund.referencedPaymentId !== payment.id && (
                          <button
                            type="button"
                            className="mt-0.5 pl-7 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={() => navigate(`/accounting/payments/${refund.referencedPaymentId}`)}
                          >
                            Original sale is linked to payment #{refund.referencedPaymentId}
                          </button>
                        )}
                      </div>
                      <div className="text-right tabular-nums flex-shrink-0 ml-2">
                        {saleGross > 0 && (
                          <div className="text-xs text-gray-400 line-through">
                            {refund.currency} {saleGross.toFixed(2)}
                          </div>
                        )}
                        <div className="text-sm font-medium text-orange-700">
                          {refund.currency} {refundedGross.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Refund-funding transfers — Revolut top-ups that fund a refund
                    whose original sale settled in a different payout. Not a
                    direct payment, not a refund — shown in orange to make the
                    relationship visible. */}
                {refundFundingActivities.map((t) => {
                  const orderLabel = t.orderNumber
                    ? `#${t.orderNumber}`
                    : t.referenceTxnId || t.transferTxnId;
                  const refundGrossAbs = t.refundGross !== null ? Math.abs(t.refundGross) : null;
                  return (
                    <div
                      key={`refund-funding-${t.transferTxnId}`}
                      data-testid={`refund-funding-${t.orderNumber || t.transferTxnId}`}
                      className="flex items-center justify-between py-2 px-3 border rounded-md bg-orange-50 border-orange-200"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                          <ArrowDownToLine className="h-4 w-4 text-orange-600 flex-shrink-0" />
                          {t.wooOrderId ? (
                            <a
                              href={getWooCommerceOrderUrl(t.wooOrderId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {orderLabel}
                            </a>
                          ) : (
                            <span className="font-medium text-sm text-gray-800">{orderLabel}</span>
                          )}
                          <GkRowMark
                            exceptions={gkExceptionsFor(gkData?.gk, {
                              orderNumber: t.orderNumber,
                              txnIds: [t.transferTxnId, t.refundTxnId].filter((x): x is string => Boolean(x)),
                            })}
                          />
                          <span className="text-sm text-gray-600 truncate">
                            {t.customerName || 'Refund-funding transfer'}
                          </span>
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 flex-shrink-0">
                            Refund-funding transfer
                          </span>
                        </div>
                        <div className="mt-0.5 pl-7 text-xs text-orange-700 tabular-nums">
                          Revolut top-up to fund a refund on this payout — not a direct payment or refund.
                          {refundGrossAbs !== null && (
                            <>
                              <span className="text-orange-500">&nbsp;·&nbsp;</span>
                              funds refund of&nbsp;{t.currency}&nbsp;{refundGrossAbs.toFixed(2)}
                            </>
                          )}
                        </div>
                        {t.referencedPaymentId && t.referencedPaymentId !== payment.id && (
                          <button
                            type="button"
                            className="mt-0.5 pl-7 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={() => navigate(`/accounting/payments/${t.referencedPaymentId}`)}
                          >
                            Original sale settled in payment #{t.referencedPaymentId}
                          </button>
                        )}
                      </div>
                      <div className="text-right tabular-nums flex-shrink-0 ml-2">
                        <div className="text-sm font-medium text-orange-700">
                          +{t.currency} {t.amount.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-400">Revolut transfer</div>
                      </div>
                    </div>
                  );
                })}

                {greenLinkedOrders.map(renderLinkedOrderRow)}

                {/* Sum row */}
                <div className="flex justify-end pt-1 border-t border-gray-100 gap-4">
                  {hasFeesData && linkedFees > 0 && (
                    <span className="text-xs text-gray-400 tabular-nums self-center">
                      fees {payment.currency} {linkedFees.toFixed(2)}
                    </span>
                  )}
                  <div className="text-right">
                    {txSummary ? (
                      <>
                        <span className="text-sm text-gray-500 mr-2">
                          {txSourceLabel} reconciled net{hasCarryover ? ' (incl. carryover)' : ''}:
                        </span>
                        <span className={`text-sm font-semibold tabular-nums ${linkedTotalClass}`}>
                          {payment.currency} {comparisonSum.toFixed(2)}
                        </span>
                      </>
                    ) : hasFeesData && linkedFees > 0 ? (
                      <>
                        <div className="text-xs text-gray-400 tabular-nums line-through">
                          {payment.currency} {linkedGross.toFixed(2)}
                        </div>
                        <div className={`text-sm font-semibold tabular-nums ${linkedTotalClass}`}>
                          {payment.currency} {linkedNet.toFixed(2)}
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-gray-500 mr-2">Total linked:</span>
                        <span className={`text-sm font-semibold tabular-nums ${linkedTotalClass}`}>
                          {payment.currency} {linkedSum.toFixed(2)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {payment.linkedOrders.length === 0 && refundOnlyGroups.length === 0 && unlinkedSettlementsCount === 0 && refundFundingCount === 0 && (
              <p className="text-sm text-gray-500">No orders linked yet.</p>
            )}
          </div>
        </div>

        {/* Linked Vasco invoices — close payments that have no b2b order (Google /
            Apple software invoices) directly against Vasco invoices. SW-2486. */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Linked Vasco Invoices</h3>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Invoice number + amount input */}
            <div>
              <label htmlFor="vasco-invoice-number" className="block text-sm font-medium text-gray-700 mb-2">
                Link a Vasco invoice
              </label>
              <div className="flex gap-2">
                <input
                  id="vasco-invoice-number"
                  type="text"
                  value={vascoNumberInput}
                  onChange={(e) => {
                    setVascoNumberInput(e.target.value);
                    if (vascoLinkError) setVascoLinkError('');
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !vascoAddDisabled) handleLinkVasco(); }}
                  onBlur={() => {
                    const trimmed = vascoNumberInput.trim();
                    if (trimmed && !trimmed.includes('/')) {
                      setVascoNumberInput(`${trimmed}/${new Date().getFullYear()}`);
                    }
                  }}
                  placeholder={`e.g. 3814/${new Date().getFullYear()}`}
                  disabled={vascoLinking || isForceClosed}
                  className="flex-1 h-10 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent disabled:bg-gray-50"
                />
                <input
                  type="number"
                  step="0.01"
                  value={vascoAmountInput}
                  onChange={(e) => { setVascoAmountInput(e.target.value); setVascoAmountTouched(true); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !vascoAddDisabled) handleLinkVasco(); }}
                  placeholder={payment.currency === 'EUR' ? 'Amount (optional)' : `Amount (${payment.currency})`}
                  title="Leave blank to claim the full invoice amount"
                  disabled={vascoLinking || isForceClosed}
                  className="w-44 h-10 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent disabled:bg-gray-50"
                />
                <Button variant="primary" onClick={handleLinkVasco} disabled={vascoAddDisabled} icon={Plus}>
                  {vascoLinking ? 'Adding…' : 'Add'}
                </Button>
              </div>

              {vascoLinkError && (
                <div className="mt-2 flex items-start text-sm text-red-600">
                  <AlertTriangle className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                  <span>{vascoLinkError}</span>
                </div>
              )}

              {vascoLookupLoading && (
                <p className="mt-2 text-xs text-gray-400">Looking up invoice…</p>
              )}

              {/* Non-EUR payments always need an explicit claimed amount */}
              {vascoNumberValid && payment.currency !== 'EUR' && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Payment is in {payment.currency}; Vasco amounts are EUR — enter the claimed amount in {payment.currency}.
                  </span>
                </div>
              )}

              {/* Lookup result: invoice found */}
              {vascoLookupInvoice && vascoLookup && (
                <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">Vasco {vascoLookupInvoice.vascoInvoice}</span>
                    {vascoLookupInvoice.buyer && (
                      <span className="text-gray-600">· {vascoLookupInvoice.buyer}</span>
                    )}
                    {vascoLookupInvoice.invoiceDate && (
                      <span className="text-gray-500">· {formatVascoDate(vascoLookupInvoice.invoiceDate)}</span>
                    )}
                    {vascoLookupInvoice.amount != null && (
                      <span className="tabular-nums text-gray-900">· EUR {vascoLookupInvoice.amount.toFixed(2)}</span>
                    )}
                    {vascoLookupInvoice.isCreditNote && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 flex-shrink-0">
                        Credit note
                      </span>
                    )}
                  </div>

                  {vascoLookup.claims.length > 0 && (
                    <div className="mt-1 text-xs text-gray-600 tabular-nums">
                      {vascoLookup.claims.map((c) => (
                        <div key={c.paymentId}>
                          Already claimed by{' '}
                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={() => navigate(`/accounting/payments/${c.paymentId}`)}
                          >
                            payment #{c.paymentId}
                          </button>
                          {' '}({formatDateTime(c.paymentDate)}, {c.paymentCurrency}{' '}
                          {c.amount.toFixed(2)})
                        </div>
                      ))}
                    </div>
                  )}

                  {vascoLookup.alreadyLinkedToThisPayment ? (
                    <div className="mt-1 text-xs text-amber-700">Already linked to this payment.</div>
                  ) : (vascoLookup.remainder ?? 0) <= 0 && vascoLookup.claims.length > 0 ? (
                    <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                      <span>This invoice is already fully claimed by other payments.</span>
                    </div>
                  ) : vascoLookup.claims.length > 0 ? (
                    <div className="mt-1 text-xs text-gray-600 tabular-nums">
                      Remainder: EUR {(vascoLookup.remainder ?? 0).toFixed(2)} unclaimed
                    </div>
                  ) : null}
                </div>
              )}

              {/* Lookup result: not in the Vasco import — free-text fallback */}
              {vascoLookup && !vascoLookup.found && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Not found in the Vasco import — enter the amount manually. The link will be flagged
                    Unverified and verified automatically on the next Vasco import.
                    {vascoLookup.claims.length > 0 && (
                      <div className="mt-1 text-xs tabular-nums">
                        {vascoLookup.claims.map((c) => (
                          <div key={c.paymentId}>
                            Already claimed by{' '}
                            <button
                              type="button"
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                              onClick={() => navigate(`/accounting/payments/${c.paymentId}`)}
                            >
                              payment #{c.paymentId}
                            </button>
                            {' '}({formatDateTime(c.paymentDate)}
                            {c.amount != null && <>, {c.paymentCurrency} {c.amount.toFixed(2)}</>})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Directly linked vasco invoice rows */}
            {vascoLinks.length > 0 ? (
              <div className="space-y-2">
                {vascoLinks.map((link) => {
                  const invoiceAmount = link.invoiceAmount != null ? parseFloat(link.invoiceAmount) : null;
                  const claimed = link.amount != null ? parseFloat(link.amount) : (invoiceAmount ?? 0);
                  const isPartial =
                    link.amount != null && invoiceAmount != null
                    && Math.abs(parseFloat(link.amount) - invoiceAmount) > 0.005;
                  return (
                    <VascoDocumentRow
                      key={link.id}
                      testId={`vasco-link-${link.invoiceNumber}`}
                      verified={link.verified}
                      invoiceNumber={link.invoiceNumber}
                      buyer={link.buyer}
                      chips={
                        <>
                          {link.verified ? (
                            <VascoChip tone="green">Verified</VascoChip>
                          ) : (
                            <VascoChip tone="amber">Unverified</VascoChip>
                          )}
                          {link.isCreditNote && <VascoChip tone="purple">Credit note</VascoChip>}
                          {link.orderNumber && <VascoChip>Order #{link.orderNumber}</VascoChip>}
                        </>
                      }
                      amount={
                        <div className="text-right tabular-nums">
                          {isPartial && invoiceAmount != null && (
                            <div className="text-xs text-gray-400 line-through">
                              EUR {invoiceAmount.toFixed(2)}
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900">
                            {payment.currency} {claimed.toFixed(2)}
                          </div>
                        </div>
                      }
                      action={
                        !isForceClosed ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleRemoveVascoLink(link.id)}
                            disabled={removingVascoLinkId === link.id}
                            icon={X}
                          >
                            Remove
                          </Button>
                        ) : null
                      }
                    />
                  );
                })}

                {/* Sum row */}
                <div className="flex justify-end pt-1 border-t border-gray-100">
                  <div className="text-right">
                    <span className="text-sm text-gray-500 mr-2">Total linked:</span>
                    <span className="text-sm font-semibold tabular-nums text-gray-900">
                      {payment.currency} {vascoLinkedNet.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : orderVascoDocuments.length === 0 ? (
              <p className="text-sm text-gray-500">No Vasco invoices linked yet.</p>
            ) : null}

            {/* Vasco documents that come with the linked orders. Read-only: they
                belong to the order, and the order link already carries their
                value, so they are listed apart from the "Total linked" sum. */}
            {orderVascoDocuments.length > 0 && (
              <div className="space-y-2" data-testid="order-vasco-documents">
                <p className="text-sm font-medium text-gray-700">From the linked orders</p>
                {orderVascoDocuments.map((doc) => (
                  <VascoDocumentRow
                    key={doc.linkId != null ? `manual-${doc.linkId}` : `import-${doc.invoiceId}`}
                    testId={`order-vasco-doc-${doc.invoiceNumber}`}
                    verified={doc.verified}
                    invoiceNumber={doc.invoiceNumber}
                    buyer={doc.buyer}
                    chips={
                      <>
                        {doc.linkedOrderNumber && (
                          <VascoChip
                            tone="blue"
                            title="This Vasco invoice belongs to a linked order"
                          >
                            Order #{doc.linkedOrderNumber}
                          </VascoChip>
                        )}
                        {doc.source === 'import' ? (
                          <VascoChip>From Vasco import</VascoChip>
                        ) : (
                          <VascoChip>Linked by hand on the order</VascoChip>
                        )}
                        {!doc.verified && <VascoChip tone="amber">Unverified</VascoChip>}
                        {doc.isCreditNote && <VascoChip tone="purple">Credit note</VascoChip>}
                      </>
                    }
                    amount={
                      doc.amount != null ? (
                        <span className="text-sm font-medium text-gray-900 tabular-nums">
                          EUR {parseFloat(doc.amount).toFixed(2)}
                        </span>
                      ) : null
                    }
                    action={
                      doc.linkedOrderId != null ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/accounting/b2b-orders/${doc.linkedOrderId}`)}
                          icon={undefined}
                        >
                          Open order
                        </Button>
                      ) : null
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Possible duplicate — invoice/order refs that are already linked to another payment */}
        {alreadyClaimed.length > 0 && (
          <div className="bg-white border border-amber-300 rounded-lg shadow-sm">
            <div className="bg-amber-50 px-6 py-4 border-b border-amber-200">
              <h3 className="text-lg font-medium text-amber-900 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Possible duplicate payment
              </h3>
              <p className="text-xs text-amber-800 mt-0.5">
                This payment's remittance references order{alreadyClaimed.length !== 1 ? 's' : ''} that {alreadyClaimed.length !== 1 ? 'are' : 'is'} already linked to another payment. Review before taking action.
              </p>
            </div>

            <div className="px-6 py-5 space-y-3">
              {alreadyClaimed.map((entry) => {
                const ref = entry.invoice_number || `#${entry.order_number}`;
                return (
                  <div
                    key={`${entry.order_id}-${entry.linked_payment_id}`}
                    className="border border-amber-200 rounded-md p-3 bg-amber-50/40"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-sm text-gray-800">
                          <span className="font-medium">{ref}</span>
                          <span className="text-gray-500"> · order </span>
                          <a
                            href={getWooCommerceOrderUrl(entry.woo_order_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            #{entry.order_number}
                          </a>
                          {entry.customer_name && (
                            <span className="text-gray-500"> · {entry.customer_name}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 tabular-nums">
                          Order total:{' '}
                          <span className="font-medium text-gray-700">
                            {entry.total != null ? `${entry.order_currency} ${entry.total.toFixed(2)}` : '—'}
                          </span>
                          {entry.order_status && (
                            <span className="ml-2 text-gray-400">· {entry.order_status}</span>
                          )}
                        </div>
                      </div>

                      <div className="text-right min-w-0">
                        <div className="text-xs text-gray-500">Already linked to</div>
                        <a
                          href={`/accounting/payments/${entry.linked_payment_id}`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline tabular-nums"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/accounting/payments/${entry.linked_payment_id}`);
                          }}
                        >
                          Payment #{entry.linked_payment_id} · {entry.linked_payment_currency}{' '}
                          {entry.linked_payment_amount.toFixed(2)}
                        </a>
                        <div className="text-xs text-gray-500 tabular-nums">
                          {formatDateTime(entry.linked_payment_date)}
                          {entry.linked_payment_counterparty && (
                            <span className="ml-1 text-gray-400">
                              · {entry.linked_payment_counterparty}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {(() => {
                      const delta = payment.amount - entry.linked_payment_amount;
                      const absDelta = Math.abs(delta);
                      if (absDelta <= 0.01 || payment.currency !== entry.linked_payment_currency) return null;
                      return (
                        <div className="mt-2 text-xs text-amber-800">
                          This payment differs by{' '}
                          <span className="font-medium tabular-nums">
                            {payment.currency} {absDelta.toFixed(2)}
                          </span>{' '}
                          {delta > 0 ? 'more' : 'less'} than the already-linked one — possible correction or duplicate.
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Candidates card — shown when reconciliation is incomplete */}
        {showCandidates && (candidatesLoading || candidates.length > 0 || tier2Omitted > 0) && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Suggested Orders</h3>
              <p className="text-xs text-gray-500 mt-0.5">Possible matches based on amount and name — review before linking.</p>
            </div>

            <div className="px-6 py-5">
              {candidatesLoading ? (
                <p className="text-sm text-gray-400">Loading suggestions…</p>
              ) : candidates.length === 0 ? (
                <p className="text-sm text-gray-500">No suggestions found.</p>
              ) : (
                <div className="space-y-2">
                  {candidates.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between py-2 px-3 bg-gray-50 border border-gray-200 rounded-md gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Link2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <a
                          href={getWooCommerceOrderUrl(c.woo_order_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-sm text-blue-600 hover:text-blue-800 hover:underline flex-shrink-0"
                        >
                          #{c.order_number}
                        </a>
                        <span className="text-sm text-gray-700 truncate">
                          {c.customer_name || ''}
                          {c.company && c.company !== c.customer_name && (
                            <span className="text-gray-400"> · {c.company}</span>
                          )}
                        </span>
                        {c.order_date && (
                          <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:inline">
                            {new Date(c.order_date).toLocaleDateString()}
                          </span>
                        )}
                        {c.status && (
                          <span className="text-xs text-gray-500 flex-shrink-0 hidden md:inline">
                            {c.status}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {c.covered_amount > 0 && c.total != null ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 flex-shrink-0">
                            Partial — {c.currency} {Math.max(0, c.total - c.covered_amount).toFixed(2)} remaining
                          </span>
                        ) : (
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                              c.tier === 0
                                ? 'bg-green-50 text-green-700'
                                : c.tier === 1
                                ? 'bg-blue-50 text-blue-700'
                                : c.tier === 3
                                ? 'bg-purple-50 text-purple-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {c.tier === 0 ? 'Invoice match' : c.tier === 1 ? 'Name + amount' : c.tier === 3 ? 'Name only' : 'Amount only'}
                          </span>
                        )}
                        <span className="text-sm font-medium tabular-nums text-gray-900 flex-shrink-0">
                          {c.total != null ? `${c.currency} ${c.total.toFixed(2)}` : '—'}
                        </span>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleLinkCandidate(c)}
                          disabled={linkingCandidateId === c.id}
                          icon={Plus}
                        >
                          {linkingCandidateId === c.id ? 'Linking…' : 'Link'}
                        </Button>
                      </div>
                    </div>
                  ))}

                  {tier2Omitted > 0 && (
                    <p className="text-xs text-gray-400 pt-1">
                      {tier2Omitted} additional amount-only candidate{tier2Omitted !== 1 ? 's' : ''} not shown (too many to be useful — use the order number field above).
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        </div>

        {/* Right column - Internal Notes (sticky sidebar) */}
        <div className="lg:col-span-1 space-y-6 lg:sticky lg:top-8 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto pb-8 custom-scrollbar">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-6 py-5">
              <PaymentInternalNotes paymentId={Number(id)} />
            </div>
          </div>
        </div>
      </div>

      {/* Force close modal — requires a mandatory note. */}
      <Modal
        isOpen={showForceCloseModal}
        onClose={() => { if (!forceClosing) { setShowForceCloseModal(false); setForceCloseNote(''); } }}
        title="Force close payment"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Force closing marks this payment as resolved and removes it from the reconciliation
            queue, even though it isn&apos;t linked to an order. Explain why — this note is kept
            for traceability.
          </p>
          <div>
            <label htmlFor="force-close-note" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Note (required)
            </label>
            <Textarea
              id="force-close-note"
              value={forceCloseNote}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForceCloseNote(e.target.value)}
              placeholder="e.g. Refund from an online purchase — no customer order to match."
              rows={4}
              disabled={forceClosing}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => { setShowForceCloseModal(false); setForceCloseNote(''); }}
              disabled={forceClosing}
              icon={undefined}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleForceClose}
              loading={forceClosing}
              disabled={!forceCloseNote.trim() || forceClosing}
              icon={Lock}
            >
              Force close
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        onConfirm={handleReopen}
        title="Reopen payment"
        message="This returns the payment to the reconciliation queue. The force-close note is kept for traceability."
        confirmText="Reopen"
        type="primary"
      />
    </div>
  );
};

export default PaymentDetail;
